import { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import {
  loadStudentPrefixes,
  saveStudentPrefixes,
  defaultStudentPrefix,
} from "../directory/StudentProfileForm";
import { setPayrollEnabledLocally } from "../../utils/payrollFeature";
import { setHomepageEnabledLocally } from "../../utils/homepageFeature";
import {
  fetchCompanyDefaults,
  saveCompanyDefaults,
  COMPANY_DEFAULTS_FALLBACK,
} from "../../utils/companyDefaults";
import s from "./SettingsPage.module.css";
import CloudflareMigrationPanel from "./CloudflareMigrationPanel";
import GradingPeriodsCard from "./GradingPeriodsCard";

/** Adds `.scrolling` while the user is actively scrolling; removes it ~700ms
 * after the last event. Paired with CSS that paints the scrollbar thumb only
 * when that class is present or the element is hovered. */
function useScrollActiveClass(activeClass) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      el.classList.add(activeClass);
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove(activeClass), 700);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); el.removeEventListener("scroll", onScroll); };
  }, [activeClass]);
  return ref;
}

const ASSIGNABLE_ROLES = [
  { value: "Admin",            label: "Admin",             hint: "Full management access",       group: "Core" },
  { value: "ContentManager",   label: "Content Manager",   hint: "Owns all website content",     group: "Core" },
  { value: "AcademicHead",     label: "Academic Head",     hint: "Assigns teachers, subjects & schedules", group: "Core" },
  { value: "Teacher",          label: "Teacher",           hint: "Grade encoding & classes",     group: "Core" },
  { value: "Student",          label: "Student",           hint: "Enrolled learner",             group: "Core" },
  { value: "Parent",           label: "Parent / Guardian", hint: "Views child's records",        group: "Core" },

  { value: "Registrar",        label: "Registrar",          hint: "Enrolment & records",         group: "Office" },
  { value: "Cashier",          label: "Cashier / Finance",  hint: "Tuition & payments",          group: "Office" },
  { value: "HR",               label: "HR / Payroll",       hint: "Staff & salaries",            group: "Office" },
  { value: "Librarian",        label: "Librarian",          hint: "Library / required books",    group: "Office" },
  { value: "Nurse",            label: "Nurse / Clinic",     hint: "Medical & health",            group: "Office" },
  { value: "GuidanceOffice",   label: "Guidance Office",    hint: "Counseling & discipline",     group: "Office" },
  { value: "ComputerLab",      label: "Computer Lab / IT",  hint: "Computer lab coordinator",    group: "Office" },
  { value: "ScienceLab",       label: "Science Lab",        hint: "Science lab coordinator",     group: "Office" },

  { value: "RegistrarStaff",   label: "Registrar Staff",    hint: "Assists Registrar",           group: "Staff" },
  { value: "CashierStaff",     label: "Cashier Staff",      hint: "Assists Cashier",             group: "Staff" },
  { value: "LibraryStaff",     label: "Library Staff",      hint: "Assists Librarian",           group: "Staff" },
  { value: "ClinicStaff",      label: "Clinic Staff",       hint: "Assists Nurse",               group: "Staff" },
  { value: "ComputerLabStaff", label: "Computer Lab Staff", hint: "Assists Computer Lab",        group: "Staff" },
  { value: "ScienceLabStaff",  label: "Science Lab Staff",  hint: "Assists Science Lab",         group: "Staff" },
  { value: "Janitor",          label: "Janitor",            hint: "Cleaning & facilities",       group: "Staff" },
  { value: "SecurityGuard",    label: "Security Guard",     hint: "Visitor logs & safety",       group: "Staff" },
];

/** Staff roles the picker offers after a Staff person is linked. Student /
 *  Parent categories lock the role to their own value, so those aren't here. */
const STAFF_ROLE_VALUES = new Set([
  "Admin","ContentManager","AcademicHead","Teacher","Registrar","Cashier","HR","Librarian","Nurse",
  "GuidanceOffice","ComputerLab","ScienceLab",
  "RegistrarStaff","CashierStaff","LibraryStaff","ClinicStaff",
  "ComputerLabStaff","ScienceLabStaff","Janitor","SecurityGuard",
]);

/** Role set a given caller is allowed to assign. SuperAdmin can assign
 *  anything in STAFF_ROLE_VALUES (Admin included). Plain Admin can assign
 *  every non-privileged role — no other Admin (horizontal escalation),
 *  no SuperAdmin (system-unique). Mirrors `isAssignableByAdmin()` on the
 *  backend so the picker and the POST /api/users guard agree. */
function rolesAssignableBy(callerRole) {
  if (callerRole === "SuperAdmin") return STAFF_ROLE_VALUES;
  const allowed = new Set(STAFF_ROLE_VALUES);
  allowed.delete("Admin");
  return allowed;
}

/** Suggest a sensible role default based on the employee's staffType. The
 *  user can still override with the dropdown — this just saves a click for
 *  the common case. */
function suggestRoleFromStaffType(staffType, role, callerRole) {
  // Plain Admins can't assign "Admin", so swap the MANAGEMENT / fallback
  // suggestions to an Admin-assignable role.
  const adminIsAllowed = callerRole === "SuperAdmin";
  if (staffType === "TEACHING")    return "Teacher";
  if (staffType === "SUPPORT")     return "Janitor";
  if (staffType === "MANAGEMENT")  return adminIsAllowed ? "Admin" : "AcademicHead";
  if (role && STAFF_ROLE_VALUES.has(role) && (adminIsAllowed || role !== "Admin")) return role;
  return adminIsAllowed ? "Admin" : "Teacher";
}

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function suggestUsername(name) {
  if (!name) return "";
  const first = name.trim().split(/\s+/)[0] || "";
  return first.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

const EMPTY_FORM = {
  username: "", email: "", password: "", role: "Teacher",
  // The person this account is linked to. A new user can't be created unless
  // this is set — see handleCreate.
  linked: null, // { type: "STAFF" | "STUDENT" | "PARENT", id, name, email?, code? }
  // Multi-tenant: which branch this new account belongs to. Only chain
  // admins (SuperDev / SuperAdmin / Owner) see the picker — for branch
  // admins the backend auto-stamps from their session's X-School-Id.
  // Empty string = "use my current branch" (the default for chain admins
  // too — the dropdown defaults to whichever school they're currently
  // viewing in the topbar).
  schoolId: "",
};

/** A single category card in the picker's first step. Uses a CSS-module
 *  tone modifier (`.tone-staff` etc.) so per-category accent colors live in
 *  the stylesheet instead of inline style. */
function CategoryTile({ tone, label, hint, icon, onClick }) {
  return (
    <button className={`${s.pickerTile} ${s[`tone_${tone}`]}`} onClick={onClick} type="button">
      <span className={s.tileIcon}>{icon}</span>
      <span className={s.tileLabel}>{label}</span>
      <span className={s.tileHint}>{hint}</span>
      <span className={s.tileArrow} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </span>
    </button>
  );
}

/** Popup that shows all assignable roles as a 3-column card grid, grouped by
 *  Core / Office / Staff. Read-only catalog (no add/edit/delete — the role
 *  string drives permissions in code, so values stay fixed). */
function RolePickerModal({ value, onClose, onPick, allowedValues }) {
  const overlayRef = useRef(null);
  const gridRef = useScrollActiveClass(s.scrolling);

  const filtered = ASSIGNABLE_ROLES.filter(r =>
    !allowedValues || allowedValues.has(r.value)
  );
  const groups = filtered.reduce((acc, r) => {
    (acc[r.group] ||= []).push(r);
    return acc;
  }, {});
  const groupOrder = ["Core", "Office", "Staff"].filter(g => groups[g]?.length);
  const groupTone = { Core: "roleCore", Office: "roleOffice", Staff: "roleStaff" };

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div className={s.pickerOverlay} ref={overlayRef}>
      <div className={`${s.pickerModal} ${s.rolePickerModal}`}>
        <div className={s.pickerHeader}>
          <div>
            <div className={s.pickerTitle}>Choose a role</div>
            <div className={s.pickerSub}>
              What this account is allowed to do. Groups are fixed — values are wired to permissions in code.
            </div>
          </div>
          <button className={s.pickerClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={s.roleGrid} ref={gridRef}>
          {groupOrder.map(group => (
            <div key={group} className={s.roleColumn}>
              <div className={s.roleColumnHead}>
                <span className={`${s.roleGroupPill} ${s[groupTone[group]]}`}>
                  {group.toUpperCase()}
                </span>
                <span className={s.roleGroupCount}>
                  {groups[group].length} role{groups[group].length === 1 ? "" : "s"}
                </span>
              </div>
              <div className={s.roleList}>
                {groups[group].map(r => (
                  <button
                    key={r.value}
                    type="button"
                    className={`${s.roleCard} ${value === r.value ? s.roleCardActive : ""}`}
                    onClick={() => onPick(r.value)}>
                    <span className={s.roleCardName}>{r.label}</span>
                    {r.hint && <span className={s.roleCardHint}>{r.hint}</span>}
                    {value === r.value && <span className={s.roleCardCheck}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Popup that walks the admin through (1) pick a category, (2) pick a person
 *  in that category. People who already have an app_user row are shown
 *  disabled so we can't accidentally create two accounts for the same person. */
function PersonPickerModal({ onClose, onPick, linkedKeys, staffOnly }) {
  // When `staffOnly` is true the picker bypasses the Staff/Student/Parent
  // category step and lands directly on the Staff list. Used by the
  // Root Admin "Link to Employee" flow — pinning a SuperAdmin account
  // to a Student or Parent record makes no sense, so the choice
  // shouldn't even be presented.
  const [step, setStep]     = useState(staffOnly ? "list" : "category");
  const [type, setType]     = useState(staffOnly ? "STAFF" : null);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState("");
  const [items, setItems]   = useState([]);
  const [search, setSearch] = useState("");
  const overlayRef          = useRef(null);

  // Auto-load Staff list on mount when the picker was opened in
  // staff-only mode (Root Admin "Link to Employee" flow). Standard
  // mode leaves the user on the category tile picker until they
  // pick one explicitly.
  useEffect(() => {
    if (staffOnly) {
      openCategory("STAFF");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffOnly]);

  async function openCategory(t) {
    setType(t); setStep("list"); setSearch(""); setError(""); setLoad(true);
    try {
      let url;
      if (t === "STAFF")        url = `${API_BASE}/api/employees?all=true`;
      else if (t === "STUDENT") url = `${API_BASE}/api/students`;
      else                      url = `${API_BASE}/api/parents`;

      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const raw = await res.json();

      // Normalize each row to a common shape regardless of source table.
      const norm = (Array.isArray(raw) ? raw : []).map(r => {
        if (t === "STAFF") {
          return {
            type: "STAFF", id: r.id, name: r.name || "Unnamed",
            email: null, code: r.code, meta: r.role || r.department || "",
            staffType: r.staffType, role: r.role,
            photoUrl: r.photoUrl || null,
          };
        }
        if (t === "STUDENT") {
          const fullName = [r.firstName, r.middleName, r.lastName].filter(Boolean).join(" ");
          return {
            type: "STUDENT", id: r.id, name: fullName || "Unnamed",
            email: null, code: r.studentNumber,
            meta: r.studentNumber ? `#${r.studentNumber}` : "",
            photoUrl: r.photoUrl || null,
          };
        }
        const fullName = [r.firstName, r.lastName].filter(Boolean).join(" ");
        return {
          type: "PARENT", id: r.id, name: fullName || "Unnamed",
          email: r.email || null, code: null,
          meta: r.occupation || r.email || "",
          photoUrl: null,
        };
      });
      setItems(norm);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoad(false);
    }
  }

  const filtered = items.filter(i => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (i.name || "").toLowerCase().includes(q)
        || (i.meta || "").toLowerCase().includes(q)
        || (i.code || "").toLowerCase().includes(q);
  });

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div className={s.pickerOverlay} ref={overlayRef}>
      <div className={s.pickerModal}>
        <div className={s.pickerHeader}>
          <div>
            <div className={s.pickerTitle}>
              {step === "category"
                ? "Link to a person"
                : staffOnly
                  ? "Link to an Employee"
                  : `Pick a ${type?.toLowerCase()}`}
            </div>
            <div className={s.pickerSub}>
              {step === "category"
                ? "Choose which directory this account belongs to."
                : staffOnly
                  ? "Pick the staff member who owns this Root Admin login (Owner / Principal)."
                  : "Select the record this account will log in as."}
            </div>
          </div>
          <button className={s.pickerClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {step === "category" && (
          <div className={s.pickerTiles}>
            <CategoryTile
              tone="staff"
              label="Staff"
              hint="Teachers, office, support"
              onClick={() => openCategory("STAFF")}
              icon={(
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                     stroke="currentColor" strokeWidth="1.7"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8h18v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8Z" />
                  <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M3 13h18" />
                </svg>
              )}
            />
            <CategoryTile
              tone="student"
              label="Student"
              hint="Enrolled students"
              onClick={() => openCategory("STUDENT")}
              icon={(
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                     stroke="currentColor" strokeWidth="1.7"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-4 9 4-9 4-9-4Z" />
                  <path d="M7 11v4c0 1.5 2.5 3 5 3s5-1.5 5-3v-4" />
                  <path d="M21 9v5" />
                </svg>
              )}
            />
            <CategoryTile
              tone="parent"
              label="Parent"
              hint="Guardians on file"
              onClick={() => openCategory("PARENT")}
              icon={(
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
                     stroke="currentColor" strokeWidth="1.7"
                     strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="8" cy="8" r="3" />
                  <circle cx="17" cy="9" r="2.5" />
                  <path d="M2 20a6 6 0 0 1 12 0" />
                  <path d="M14 20a5 5 0 0 1 8-3.5" />
                </svg>
              )}
            />
          </div>
        )}

        {step === "list" && (
          <>
            <div className={s.pickerToolbar}>
              {/* Hide the "back to categories" affordance in staff-only
                  mode — there is no category step to return to. */}
              {!staffOnly && (
                <button className={s.pickerBack} onClick={() => { setStep("category"); setType(null); }}>
                  ← Categories
                </button>
              )}
              <input
                className={s.pickerSearch}
                placeholder="Search by name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className={s.pickerList}>
              {loading && <div className={s.pickerEmpty}>Loading…</div>}
              {!loading && error && <div className={s.pickerEmpty} style={{ color: "#c0392b" }}>{error}</div>}
              {!loading && !error && filtered.length === 0 && (
                <div className={s.pickerEmpty}>No matches.</div>
              )}
              {!loading && !error && filtered.map(p => {
                const key = `${p.type}:${p.id}`;
                const alreadyLinked = linkedKeys.has(key);
                return (
                  <button
                    key={key}
                    className={`${s.pickerItem} ${alreadyLinked ? s.pickerItemDisabled : ""}`}
                    disabled={alreadyLinked}
                    onClick={() => onPick(p)}>
                    {p.photoUrl
                      ? <img src={p.photoUrl} alt="" className={s.pickerAvatarImg} />
                      : <div className={s.pickerAvatar} style={{ background: avatarColor(p.name) }}>
                          {initials(p.name)}
                        </div>
                    }
                    <div className={s.pickerItemBody}>
                      <div className={s.pickerItemName}>{p.name}</div>
                      {p.meta && <div className={s.pickerItemMeta}>{p.meta}</div>}
                    </div>
                    {alreadyLinked && <span className={s.linkedChip}>Linked</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Inline edit modal for an existing user. Supports resetting the password,
 *  changing username / email, and re-picking a role (for non-Super-Admin
 *  accounts). The backend's PUT /api/users/{id} accepts partial updates —
 *  password is only sent if the admin typed a new one. */
function EditUserModal({ user, onClose, onSaved, linkedKeys, schoolsById }) {
  const callerRole = getUser()?.role;
  // Multi-tenant: chain admins (SuperDev / SuperAdmin / Owner) can
  // transfer this user to a different branch. Branch admins can't —
  // the dropdown is hidden for them.
  const callerIsChainAdmin = callerRole === "SuperDev"
                          || callerRole === "SuperAdmin"
                          || callerRole === "Owner";
  const isSuper = user.role === "SuperAdmin";
  // Only the bootstrap-flagged Root Admin has a locked role. A
  // duplicate non-root SuperAdmin row (legacy data, direct DB inserts)
  // remains demotable so the operator can converge the install to
  // "exactly one SuperAdmin = the root". The backend enforces the
  // same rule — defense in depth.
  const isRootAdmin = !!user.isRoot;
  const [username, setUsername] = useState(user.username || "");
  const [email, setEmail]       = useState(user.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole]         = useState(user.role || "Teacher");
  const [linked, setLinked]     = useState(
    user.linkedEntityType && user.linkedEntityId
      ? {
          type: user.linkedEntityType,
          id:   user.linkedEntityId,
          name: user.linkedEntityName || "",
          code: user.linkedEntityCode || "",
          photoUrl: user.linkedEntityPhoto || "",
          meta: user.linkedEntityCode ? `#${user.linkedEntityCode}` : "",
        }
      : null
  );
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const [personPickerOpen, setPersonPickerOpen] = useState(false);
  // Branch / school assignment. Null on the user row means
  // "chain-wide" (the bootstrap default for SuperDev / SuperAdmin /
  // Owner). The select serialises null as empty string so React
  // renders the controlled value correctly.
  const [schoolId, setSchoolId] = useState(
    user.schoolId == null ? "" : String(user.schoolId)
  );
  const overlayRef = useRef(null);

  // Role is locked by the currently linked entity's category — Student and
  // Parent always map to their fixed role; Staff allows the role picker.
  const roleLocked = linked?.type === "STUDENT" || linked?.type === "PARENT";
  const currentRoleMeta = ASSIGNABLE_ROLES.find(r => r.value === role);

  // Exclude THIS user's current link from the "already linked" greyed-out
  // list in the picker — otherwise re-selecting the same person would be
  // disabled.
  const pickerLinkedKeys = new Set(linkedKeys || []);
  if (linked) pickerLinkedKeys.delete(`${linked.type}:${linked.id}`);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose, saving]);

  function handleRelink(person) {
    setPersonPickerOpen(false);
    setLinked(person);
    // Role auto-derives from the picked entity type — except when the
    // target user is the Root Admin. That role is locked (server
    // enforces "Root Admin's role cannot be changed") so we leave
    // the role state alone and let the picker only feed staff-only
    // choices anyway. Non-root SuperAdmins fall through so the
    // operator can swap the link AND demote in one save.
    if (isRootAdmin) return;
    if (person.type === "STUDENT")      setRole("Student");
    else if (person.type === "PARENT")  setRole("Parent");
    else                                 setRole(suggestRoleFromStaffType(person.staffType, person.role, callerRole));
  }

  async function save() {
    if (!username.trim()) { setError("Username is required."); return; }
    setSaving(true); setError("");
    try {
      const body = { username: username.trim(), email: email.trim() };
      if (password) body.password = password;
      // Role is sent for everyone EXCEPT the Root Admin. Non-root
      // SuperAdmins can be demoted; the backend's "Only one Super
      // Admin allowed" rule prevents accidental promotions in either
      // direction so this is safe.
      if (!isRootAdmin) body.role = role;

      // Re-linking is only sent when it actually changed, to keep the PUT
      // body minimal (and avoid accidentally clobbering linkage on the server).
      const linkChanged =
        linked?.type !== user.linkedEntityType ||
        (linked?.id ?? null) !== (user.linkedEntityId ?? null);
      // Linkage is sent for ANY user when it changed — including the
      // SuperAdmin / Root Admin so the operator can pin the bootstrap
      // login to its real-world owner (Owner / Principal). The picker
      // is restricted to STAFF-only when isSuper, so a Student or
      // Parent can never end up here, which keeps the role rule
      // "SuperAdmin must stay STAFF-or-null" intact.
      if (linkChanged) {
        body.linkedEntityType = linked?.type ?? null;
        body.linkedEntityId   = linked?.id   ?? null;
        body.employeeCode     = linked?.type === "STAFF" ? (linked.code || "") : "";
      }

      // Multi-tenant: branch transfer. Only included in the PUT body
      // when the chain admin actually changed it AND the current
      // role is a branch-tier role (Chain-tier roles have school_id
      // forced to null on the backend regardless of body, so sending
      // it for those is harmless but noisy.) The backend rejects
      // cross-school transfers from branch admins with 403, so this
      // is also gated client-side to avoid surfacing a confusing
      // error to non-chain-admins.
      if (callerIsChainAdmin) {
        const newSchoolId = schoolId === "" ? null : Number(schoolId);
        const oldSchoolId = user.schoolId == null ? null : Number(user.schoolId);
        if (newSchoolId !== oldSchoolId) {
          body.schoolId = newSchoolId;
        }
      }

      const res = await fetch(`${API_BASE}/api/users/${user.id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      onSaved(`User "${username}" updated.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={s.pickerOverlay} ref={overlayRef}>
      <div className={`${s.pickerModal} ${s.editUserModal}`}>
        <div className={s.pickerHeader}>
          <div>
            <div className={s.pickerTitle}>Edit user</div>
            <div className={s.pickerSub}>
              {isRootAdmin
                ? "Root Admin — username, email, password, and the linked employee can be changed. Role is locked to Super Admin."
                : isSuper
                  ? "Duplicate Super Admin — demote to Admin / Cashier / etc. so only the Root Admin holds the role. Backend enforces \"only one Super Admin allowed\"."
                  : "Update this account's details or reset its password."}
            </div>
          </div>
          <button className={s.pickerClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={s.editBody}>
          {/* Linked person section — shown for everyone. For the Root
              Admin specifically, the picker is gated to STAFF-only
              (see staffOnly prop below) so the bootstrap login can
              only be pinned to an Employee record (Owner / Principal).
              Non-root SuperAdmins and other roles get the full three-
              category picker. */}
          <div className={s.field}>
              <span>{isRootAdmin ? "Linked employee" : "Linked person"}</span>
              {linked ? (
                <div className={s.linkedChipRow}>
                  {linked.photoUrl
                    ? <img src={linked.photoUrl} alt="" className={s.linkedAvatarImg} />
                    : <div className={s.linkedAvatar} style={{ background: avatarColor(linked.name) }}>
                        {initials(linked.name)}
                      </div>
                  }
                  <div className={s.linkedBody}>
                    <div className={s.linkedName}>{linked.name || "(no name)"}</div>
                    <div className={s.linkedMeta}>
                      {linked.type === "STAFF"   && "Staff"}
                      {linked.type === "STUDENT" && "Student"}
                      {linked.type === "PARENT"  && "Parent"}
                      {linked.meta ? ` · ${linked.meta}` : ""}
                    </div>
                  </div>
                  <div className={s.linkedActions}>
                    <button type="button" className={s.linkedChange}
                            onClick={() => setPersonPickerOpen(true)}
                            title="Pick a different person to link this account to">
                      Change
                    </button>
                    {/* Unlink — clears the link entirely. Useful when:
                        (a) the linked employee was deleted (the chip shows
                        a no-name placeholder until cleared), or (b) a fresh
                        database has no employees yet, or (c) the SuperAdmin
                        wants the Root Admin to live without an employee
                        record temporarily. Save sends
                        linkedEntityType=null + linkedEntityId=null. */}
                    <button type="button" className={s.linkedUnlink}
                            onClick={() => setLinked(null)}
                            title="Remove the link — this account will no longer point at any employee/student/parent record">
                      Unlink
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className={s.linkTile}
                        onClick={() => setPersonPickerOpen(true)}>
                  <span className={s.linkTileIcon}>＋</span>
                  <span className={s.linkTileText}>
                    <span className={s.linkTileTitle}>
                      {isRootAdmin ? "Link to an Employee" : "Link to a person"}
                    </span>
                    <span className={s.linkTileHint}>
                      {isRootAdmin
                        ? "Pin this Root Admin login to its real owner (Owner / Principal)"
                        : "Pick from Staff, Student, or Parent"}
                    </span>
                  </span>
                </button>
              )}
            </div>

          <label className={s.field}>
            <span>Username</span>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="off"
            />
          </label>

          <label className={s.field}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="off"
            />
          </label>

          <label className={s.field}>
            <span>New password <em className={s.editOptional}>(leave blank to keep current)</em></span>
            <input
              type="text"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="minimum 4 characters"
              autoComplete="new-password"
            />
          </label>

          <div className={s.field}>
            <span>Role</span>
            {isRootAdmin ? (
              /* Root Admin's role is the only one truly locked. Any
                 non-root SuperAdmin (rare, legacy data) falls through
                 to the regular role picker so the operator can demote
                 duplicates and clean up the install. */
              <div className={s.lockedRole} title="Root Admin role is fixed — this is the system's bootstrap account.">
                Super Admin
                <span className={s.lockedRoleLock}>🔒</span>
              </div>
            ) : roleLocked ? (
              <div className={s.lockedRole} title="Role is fixed by the linked category">
                {linked?.type === "STUDENT" ? "Student" : "Parent / Guardian"}
                <span className={s.lockedRoleLock}>🔒</span>
              </div>
            ) : (
              <button type="button" className={s.roleChipBtn}
                      onClick={() => setRolePickerOpen(true)}>
                <span className={s.roleChipBody}>
                  <span className={s.roleChipName}>
                    {currentRoleMeta?.label || role}
                  </span>
                  <span className={s.roleChipMeta}>
                    {currentRoleMeta?.group || "—"}
                    {currentRoleMeta?.hint ? ` · ${currentRoleMeta.hint}` : ""}
                  </span>
                </span>
                <span className={s.roleChipChange}>Change</span>
              </button>
            )}
          </div>

          {/* Branch / school transfer — chain admin only. For chain-
              tier roles on the EDITED account (SuperDev / SuperAdmin /
              Owner) the dropdown locks to "Chain-wide" because the
              backend forces school_id = NULL for those rows. */}
          {callerIsChainAdmin && (
            <label className={s.field}>
              <span>Branch / school</span>
              <select
                value={schoolId}
                onChange={e => setSchoolId(e.target.value)}
                disabled={
                  role === "SuperDev"
                  || role === "SuperAdmin"
                  || role === "Owner"
                }
              >
                {(role === "SuperDev"
                  || role === "SuperAdmin"
                  || role === "Owner") ? (
                  <option value="">Chain-wide (no pinned branch)</option>
                ) : (
                  <>
                    {Object.values(schoolsById || {})
                      .filter(sc => sc.active || String(sc.id) === schoolId)
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map(sc => (
                        <option key={sc.id} value={sc.id}>
                          {sc.shortName || sc.name}
                          {sc.active ? "" : " (inactive)"}
                        </option>
                      ))}
                  </>
                )}
              </select>
            </label>
          )}

          {error && <div className={s.errorText} style={{ marginTop: 4 }}>{error}</div>}
        </div>

        <div className={s.editFoot}>
          <button className={s.linkedChange} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={s.primaryBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {rolePickerOpen && (
        <RolePickerModal
          value={role}
          allowedValues={rolesAssignableBy(callerRole)}
          onClose={() => setRolePickerOpen(false)}
          onPick={v => { setRole(v); setRolePickerOpen(false); }}
        />
      )}

      {personPickerOpen && (
        <PersonPickerModal
          linkedKeys={pickerLinkedKeys}
          // Restrict to STAFF-only ONLY for the Root Admin — pinning the
          // bootstrap login to a Student or Parent record is nonsensical.
          // Non-root SuperAdmins (legacy duplicates being cleaned up) get
          // the full three-category picker since they may be demoting
          // and re-linking in the same save.
          staffOnly={isRootAdmin}
          onClose={() => setPersonPickerOpen(false)}
          onPick={handleRelink}
        />
      )}
    </div>
  );
}

/** Every tab carries a `superAdminOnly` flag — tabs whose backend refuses
 *  non-SuperAdmin requests (currently just Maintenance) stay hidden for
 *  plain Admins so they never hit a 403 wall. Users is now open to Admins
 *  too, but the UI filters out SuperAdmin rows and the Admin role from
 *  the picker so they can't escalate privileges. */
// ── Inline-style primitives reused by the Employee Defaults tab ──
const inputStyle = {
  background: "#fff",
  border: "1.5px solid #e8e4dc",
  borderRadius: 10,
  padding: "8px 12px",
  fontSize: 14,
  height: 36,
  boxSizing: "border-box",
  outline: "none",
};
const selectStyle = { ...inputStyle, height: 38, paddingRight: 28 };

/** "Hour : Minute AM/PM" picker bound to a "HH:mm" string. Mirrors
 *  the InventorySales Settings → Employee Defaults UI. */
function TimeOfDayField({ label, value, onChange }) {
  const [h24, m] = (value || "").split(":").map(n => parseInt(n, 10));
  const ampm = h24 >= 12 ? "PM" : "AM";
  const hr12 = isNaN(h24) ? "" : String(h24 % 12 || 12);
  const min  = isNaN(m)   ? "" : String(m).padStart(2, "0");
  function update(nh, nm, na) {
    const hh = nh === "" ? "" : nh;
    if (hh === "") { onChange(""); return; }
    let h = parseInt(hh, 10);
    if (na === "PM" && h !== 12) h += 12;
    if (na === "AM" && h === 12) h = 0;
    const mm = (nm === "" || isNaN(parseInt(nm, 10))) ? "00" : String(parseInt(nm, 10)).padStart(2, "0");
    onChange(`${String(h).padStart(2, "0")}:${mm}`);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>{label}</label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 12px 1fr 80px", gap: 6, alignItems: "center" }}>
        <input
          type="number" min="1" max="12"
          value={hr12}
          onChange={e => update(e.target.value, min, ampm)}
          style={{ ...inputStyle, textAlign: "center" }}
        />
        <span style={{ textAlign: "center", color: "#8a8070" }}>:</span>
        <input
          type="number" min="0" max="59"
          value={min}
          onChange={e => update(hr12, e.target.value, ampm)}
          style={{ ...inputStyle, textAlign: "center" }}
        />
        <select
          value={ampm}
          onChange={e => update(hr12 || "8", min || "00", e.target.value)}
          style={selectStyle}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

const SETTINGS_TABS = [
  { key: "users",       label: "Users",                superAdminOnly: false },
  { key: "academic",    label: "Academic Settings",    superAdminOnly: false },
  { key: "soa",         label: "Statement of Account", superAdminOnly: false },
  { key: "employee",    label: "Employee Defaults",    superAdminOnly: true,  payrollOnly: true },
  { key: "maintenance", label: "Maintenance",          superAdminOnly: true  },
];

export default function SettingsPage() {
  const currentRole = getUser()?.role;
  // Chain-READ tier — SuperDev, SuperAdmin, and Owner can pick any
  // branch when creating / editing a user. Branch admins (Admin and
  // below) don't see the picker — the backend auto-stamps from
  // their X-School-Id header.
  const isChainAdmin = currentRole === "SuperDev"
                    || currentRole === "SuperAdmin"
                    || currentRole === "Owner";
  // SuperDev (project developer) is a hidden role that inherits every
  // SuperAdmin capability. The webapp never advertises its existence —
  // there is no role-picker entry, no "SuperDev-only" hint anywhere in
  // the UI, and SuperDev rows are filtered out of the user list by the
  // backend. `isSuperDev` is used here only to UNLOCK the destructive
  // Maintenance actions (Import / Wipe / orphan cleanup) — a SuperAdmin
  // sees the Maintenance tab too, but with only the safe Backup card.
  const isSuperDev   = currentRole === "SuperDev";
  // Treat SuperDev as a SuperAdmin for every gate that's already in
  // place — they should be able to do anything a SuperAdmin can plus
  // the extras gated by `isSuperDev`.
  const isSuperAdmin = currentRole === "SuperAdmin" || isSuperDev;
  const [activeTab, setActiveTab] = useState("academic");
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [busy,    setBusy]    = useState(false);
  const [flash,   setFlash]   = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Multi-tenant: load the school catalog so each user row can show a
  // friendly school name instead of a bare numeric id. Chain admins
  // (the only role that sees the Users tab) need this to identify
  // which branch each account belongs to at a glance.
  const [schoolsById, setSchoolsById] = useState({});
  useEffect(() => {
    fetch(`${API_BASE}/api/schools/all`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const map = {};
        for (const s of (list || [])) map[s.id] = s;
        setSchoolsById(map);
      })
      .catch(() => {});
  }, []);
  const scrollRef = useScrollActiveClass(s.scrolling);

  /** Set of "TYPE:ID" keys for people who already have an account, so the
   *  picker can grey them out. Includes the legacy employeeCode -> STAFF map
   *  by looking up the code in the employees list, but we don't need to do
   *  that on the client because the picker already shows a "Linked" chip
   *  based on linkedEntityType / linkedEntityId once an account is created
   *  via this flow. */
  const linkedKeys = new Set(
    users
      .filter(u => u.linkedEntityType && u.linkedEntityId)
      .map(u => `${u.linkedEntityType}:${u.linkedEntityId}`)
  );

  // ── Maintenance (SuperAdmin-only; this whole page is gated to SuperAdmin) ──
  const [maintBusy,    setMaintBusy]    = useState("");   // "export" | "import" | "wipe" | "orphans" | ""
  const [maintMsg,     setMaintMsg]     = useState("");
  const [maintErr,     setMaintErr]     = useState("");
  const [importFile,   setImportFile]   = useState(null);
  const [importConfirm, setImportConfirm] = useState("");
  const [wipeConfirm,  setWipeConfirm]  = useState("");
  const importInputRef = useRef(null);

  // Orphan tuition payments — rows whose invoice was deleted before the
  // cascade fix in TuitionInvoiceController.delete landed. They show up in
  // FinanceTab as "Orphan payment · invoice #X (deleted)" and need an admin
  // to clear them (or the SQL equivalent).
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanConfirm, setOrphanConfirm]   = useState("");

  // ── Student ID prefixes (shared with Admissions dropdown) ──
  const [prefixes,   setPrefixes]   = useState(() => loadStudentPrefixes([]));
  const [prefixDraft, setPrefixDraft] = useState("");
  const [prefixMsg,  setPrefixMsg]  = useState("");
  const [prefixErr,  setPrefixErr]  = useState("");

  // ── Payroll module toggle ──
  // Optional HR/Payroll/Timesheet/Calendar feature ported from the
  // inventorySales project. Hidden by default; enabling flips on the
  // /salary, /timesheet, /payroll-calendar routes and the topbar nav
  // for SuperAdmin. Persisted as the `payroll.enabled` site_content key
  // so the value survives reboots and is shared across every browser
  // session. Lives here on the Maintenance tab because it's a SuperAdmin-
  // only system switch (same audience as Wipe / Restore).
  const [payrollEnabled,    setPayrollEnabled]    = useState(false);
  const [payrollBusy,       setPayrollBusy]       = useState(false);
  const [payrollFlash,      setPayrollFlash]      = useState("");
  const [payrollErr,        setPayrollErr]        = useState("");

  // Public-homepage feature flag — controls whether `/`, `/news/:id`,
  // `/programs/:slug` resolve to the marketing landing page (ON) or
  // silently redirect to /login so the system reads as members-only
  // (OFF). Persisted as `homepage.enabled` on the same site_content
  // map. Default is ON: missing key reads as enabled so existing
  // installs keep their public homepage on first deploy of this
  // toggle. See utils/homepageFeature.js for the polarity.
  const [homepageEnabled,   setHomepageEnabled]   = useState(true);
  const [homepageBusy,      setHomepageBusy]      = useState(false);
  const [homepageFlash,     setHomepageFlash]     = useState("");
  const [homepageErr,       setHomepageErr]       = useState("");

  // ── Employee Defaults (Payroll module — gates the per-employee
  // override fields on the People page). 8 keys live in /api/site-content.
  const [empDefaults, setEmpDefaults] = useState({ ...COMPANY_DEFAULTS_FALLBACK });
  const [empDefaultsBusy, setEmpDefaultsBusy] = useState(false);
  const [empDefaultsFlash, setEmpDefaultsFlash] = useState("");
  const [empDefaultsErr, setEmpDefaultsErr] = useState("");
  const [defaultLeaveRows, setDefaultLeaveRows] = useState([]);

  useEffect(() => {
    fetchCompanyDefaults().then(d => {
      setEmpDefaults({ ...COMPANY_DEFAULTS_FALLBACK, ...d });
      try {
        const raw = d["work.defaultLeaves"];
        const parsed = raw ? JSON.parse(raw) : [];
        setDefaultLeaveRows(Array.isArray(parsed) ? parsed.map(r => ({
          name: r?.name || "",
          days: r?.days != null ? String(r.days) : "",
          convertible: !!r?.convertible,
        })) : []);
      } catch { setDefaultLeaveRows([]); }
    });
  }, []);

  function setEmpDefault(key, value) {
    setEmpDefaults(prev => ({ ...prev, [key]: value }));
  }

  async function saveEmployeeDefaults() {
    setEmpDefaultsBusy(true);
    setEmpDefaultsFlash("");
    setEmpDefaultsErr("");
    try {
      const cleanedLeaves = defaultLeaveRows
        .map(r => ({
          name: (r.name || "").trim(),
          days: Number(r.days) || 0,
          convertible: !!r.convertible,
        }))
        .filter(r => r.name);
      const partial = {
        "work.defaultSchedStart":              empDefaults["work.defaultSchedStart"] || "08:00",
        "work.defaultSchedEnd":                empDefaults["work.defaultSchedEnd"]   || "17:00",
        "work.defaultOvertimeMinutes":         String(empDefaults["work.defaultOvertimeMinutes"] || "30"),
        "work.defaultPolicy":                  empDefaults["work.defaultPolicy"] || "STRICT",
        "work.defaultSalaryType":              empDefaults["work.defaultSalaryType"] || "monthly",
        "work.defaultApplyDeductionsOnDayOff": String(empDefaults["work.defaultApplyDeductionsOnDayOff"] || "false"),
        "work.defaultLeaves":                  cleanedLeaves.length ? JSON.stringify(cleanedLeaves) : "",
        "work.defaultLeaveConversionPercent":  String(empDefaults["work.defaultLeaveConversionPercent"] || "100"),
      };
      await saveCompanyDefaults(partial, currentRole);
      setEmpDefaultsFlash("Employee defaults saved.");
    } catch (e) {
      setEmpDefaultsErr(e.message || "Save failed.");
    } finally {
      setEmpDefaultsBusy(false);
    }
  }

  // Tabs the current viewer is allowed to see. Filtered for both role
  // (SuperAdmin-only tabs hidden from plain Admin) AND feature flag
  // (Employee Defaults stays out of sight when the Payroll module is
  // off, mirroring how the Topbar gates the Payroll nav group).
  const visibleTabs = SETTINGS_TABS.filter(t => {
    if (t.superAdminOnly && !isSuperAdmin) return false;
    if (t.payrollOnly && !payrollEnabled) return false;
    return true;
  });

  async function loadPayrollEnabled() {
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      setPayrollEnabled(String(map["payroll.enabled"] || "").toLowerCase() === "true");
    } catch { /* leave default */ }
  }

  async function savePayrollEnabled(next) {
    setPayrollBusy(true); setPayrollFlash(""); setPayrollErr("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ "payroll.enabled": next ? "true" : "false" }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setPayrollEnabled(next);
      // Broadcast to the in-memory cache so the topbar / route guards
      // pick up the change without a full reload.
      setPayrollEnabledLocally(next);
      setPayrollFlash(next ? "Payroll module enabled." : "Payroll module disabled.");
      setTimeout(() => setPayrollFlash(""), 3000);
    } catch (e) { setPayrollErr(e.message); }
    finally { setPayrollBusy(false); }
  }

  // ── Public homepage flag — load + save mirror the payroll pattern.
  // Default polarity is ON: a missing key reads as "true" so an empty
  // site_content table still serves the homepage on first deploy.
  async function loadHomepageEnabled() {
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      const raw = map["homepage.enabled"];
      // Treat missing / empty / unrecognised as ON, only the literal
      // string "false" flips OFF. Matches the polarity baked into
      // utils/homepageFeature.js so the toggle and the route guard
      // never disagree.
      setHomepageEnabled(raw == null ? true : String(raw).toLowerCase() !== "false");
    } catch { /* leave default ON */ }
  }

  async function saveHomepageEnabled(next) {
    setHomepageBusy(true); setHomepageFlash(""); setHomepageErr("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ "homepage.enabled": next ? "true" : "false" }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setHomepageEnabled(next);
      // Broadcast so RequirePublicHomepage flips immediately without
      // a full reload — same pattern as the Payroll toggle.
      setHomepageEnabledLocally(next);
      setHomepageFlash(next
        ? "Public homepage enabled — / now serves the landing page."
        : "Public homepage disabled — / now redirects to /login.");
      setTimeout(() => setHomepageFlash(""), 3500);
    } catch (e) { setHomepageErr(e.message); }
    finally { setHomepageBusy(false); }
  }

  // ── Role Permissions matrix (SuperDev only) ──
  // SuperDev grants additional roles the ability to:
  //   permissions.addSchools    — add / edit / activate / deactivate
  //                               rows in the schools catalog (hard
  //                               delete stays SuperDev-only).
  //   permissions.manageContent — create / edit / delete articles +
  //                               other public-site content.
  // Both values are JSON arrays of role names persisted in site_content;
  // SiteContentController gates the keys to SuperDev-only writes.
  // The backend's RolePermissions helper merges these with the hard-
  // coded baseline (e.g. Admin always manages content) at request time.
  //
  // The matrix is PURELY ADDITIVE — checking a box can never reduce a
  // role's existing privileges. Unchecking removes only what this
  // matrix added, not what the controller's hard-coded baseline grants.
  const ALL_ROLES_FOR_MATRIX = useMemo(() => [
    // SuperDev / SuperAdmin omitted — their baseline already covers
    // these permissions and offering a checkbox would be misleading.
    "Admin",
    "Owner",
    "ContentManager",
    "AcademicHead",
    "Teacher",
    "Student",
    "Parent",
    "Registrar",
    "RegistrarStaff",
    "Cashier",
    "CashierStaff",
    "HR",
    "Librarian",
    "LibraryStaff",
    "Nurse",
    "ClinicStaff",
    "GuidanceOffice",
    "ComputerLab",
    "ComputerLabStaff",
    "ScienceLab",
    "ScienceLabStaff",
    "Janitor",
    "SecurityGuard",
  ], []);

  const [permsAddSchools,    setPermsAddSchools]    = useState([]);
  const [permsManageContent, setPermsManageContent] = useState([]);
  // Login-block list — INVERSE semantic from the other two. Empty
  // array = everyone can log in (default). Adding a role disables it.
  // The UI panel below shows "Allow logging in" checkboxes whose
  // checked-state is the INVERSE of presence in this list (checked =
  // allowed = NOT in disabled list; unchecked = blocked = IN list).
  const [permsDisabledLogin, setPermsDisabledLogin] = useState([]);
  const [permsBusy,  setPermsBusy]  = useState(false);
  const [permsFlash, setPermsFlash] = useState("");
  const [permsErr,   setPermsErr]   = useState("");

  /** Lenient JSON-array parse — tolerates the same shapes the backend
   *  helper does (missing key, empty string, trailing commas). The
   *  matrix should never crash the page on a malformed value. */
  const parseRoleArray = (raw) => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(x => typeof x === "string") : [];
    } catch { return []; }
  };

  async function loadRolePermissions() {
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      setPermsAddSchools(parseRoleArray(map["permissions.addSchools"]));
      setPermsManageContent(parseRoleArray(map["permissions.manageContent"]));
      setPermsDisabledLogin(parseRoleArray(map["permissions.disabledLoginRoles"]));
    } catch { /* leave defaults empty */ }
  }

  async function saveRolePermissions(next) {
    setPermsBusy(true); setPermsFlash(""); setPermsErr("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          "permissions.addSchools":         JSON.stringify(next.addSchools),
          "permissions.manageContent":      JSON.stringify(next.manageContent),
          "permissions.disabledLoginRoles": JSON.stringify(next.disabledLogin),
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setPermsAddSchools(next.addSchools);
      setPermsManageContent(next.manageContent);
      setPermsDisabledLogin(next.disabledLogin);
      setPermsFlash("Role permissions saved.");
      setTimeout(() => setPermsFlash(""), 3000);
    } catch (e) { setPermsErr(e.message); }
    finally { setPermsBusy(false); }
  }

  /** Toggle a single role in a single permission key. Optimistic UI —
   *  we update local state immediately, then send to the backend. On
   *  failure the error banner appears and the user can retry.
   *
   *  Three permission keys with two different semantics:
   *    - addSchools / manageContent — additive: presence = granted
   *    - allowLogin — INVERSE: presence in the disabled list = blocked,
   *      so toggling on the "Allow logging in" checkbox REMOVES from
   *      the disabled list, and toggling off ADDS to it. The handler
   *      below unifies both shapes so the caller doesn't have to think
   *      about the flip. */
  function togglePermissionRole(permission, role) {
    let nextAdd      = permsAddSchools;
    let nextManage   = permsManageContent;
    let nextDisabled = permsDisabledLogin;
    if (permission === "addSchools") {
      nextAdd = permsAddSchools.includes(role)
          ? permsAddSchools.filter(r => r !== role)
          : [...permsAddSchools, role];
    } else if (permission === "manageContent") {
      nextManage = permsManageContent.includes(role)
          ? permsManageContent.filter(r => r !== role)
          : [...permsManageContent, role];
    } else if (permission === "allowLogin") {
      // Inverted: toggle = flip presence in the disabled list.
      // checkbox-checked means "allowed" which means "NOT in
      // disabled list".
      nextDisabled = permsDisabledLogin.includes(role)
          ? permsDisabledLogin.filter(r => r !== role)
          : [...permsDisabledLogin, role];
    }
    saveRolePermissions({
      addSchools:    nextAdd,
      manageContent: nextManage,
      disabledLogin: nextDisabled,
    });
  }

  // ── Statement of Account footer (officer name / title / note) ──
  // Persisted in the site_content key/value table so every SoA render reads
  // the same config. Edit surface lives here so the SoA print view stays
  // uncluttered for cashiers browsing student records.
  const [soaFooter, setSoaFooter] = useState({ officerName: "", officerTitle: "", note: "" });
  const [soaLoading, setSoaLoading] = useState(false);
  const [soaBusy,   setSoaBusy]   = useState(false);
  const [soaFlash,  setSoaFlash]  = useState("");
  const [soaErr,    setSoaErr]    = useState("");

  const ALL_MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const [soaStartMonth,   setSoaStartMonth]   = useState("August");
  const [startMonthBusy,  setStartMonthBusy]  = useState(false);
  const [startMonthFlash, setStartMonthFlash] = useState("");
  const [startMonthErr,   setStartMonthErr]   = useState("");

  async function loadSoaFooter() {
    setSoaLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      setSoaFooter({
        officerName:  map["soa.officer.name"]  || "",
        officerTitle: map["soa.officer.title"] || "",
        note:         map["soa.footer.note"]   || "",
      });
      setSoaStartMonth(map["soa.start.month"] || "August");
    } catch { /* leave defaults */ }
    finally { setSoaLoading(false); }
  }

  async function saveSoaFooter() {
    setSoaBusy(true); setSoaFlash(""); setSoaErr("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          "soa.officer.name":  soaFooter.officerName  || "",
          "soa.officer.title": soaFooter.officerTitle || "",
          "soa.footer.note":   soaFooter.note         || "",
        }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      // Re-read so we display what the DB actually persisted.
      await loadSoaFooter();
      setSoaFlash("Footer saved.");
      setTimeout(() => setSoaFlash(""), 3000);
    } catch (e) { setSoaErr(e.message); }
    finally { setSoaBusy(false); }
  }

  async function saveStartMonth() {
    setStartMonthBusy(true); setStartMonthFlash(""); setStartMonthErr("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ "soa.start.month": soaStartMonth }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setStartMonthFlash("Start month saved.");
      setTimeout(() => setStartMonthFlash(""), 3000);
    } catch (e) { setStartMonthErr(e.message); }
    finally { setStartMonthBusy(false); }
  }

  // ── Accepted banks (dropdown options in the tuition Pay/Refund modals) ──
  // Stored as a JSON array under the tuition.banks key in site_content so
  // admins manage the canonical list here and cashiers pick from it — no
  // free-text means no "BDO" vs "bdo" vs "B.D.O." drift in the history log.
  const [banks,      setBanks]      = useState([]);
  const [bankDraft,  setBankDraft]  = useState("");
  const [banksBusy,  setBanksBusy]  = useState(false);
  const [banksFlash, setBanksFlash] = useState("");
  const [banksErr,   setBanksErr]   = useState("");

  async function loadBanks() {
    try {
      const res = await fetch(`${API_BASE}/api/site-content`);
      if (!res.ok) return;
      const map = await res.json();
      const raw = map["tuition.banks"];
      if (!raw) { setBanks([]); return; }
      try {
        const arr = JSON.parse(raw);
        setBanks(Array.isArray(arr) ? arr.filter(x => typeof x === "string" && x.trim()) : []);
      } catch {
        // Legacy fallback — some admin may have seeded this as newline-separated.
        setBanks(String(raw).split("\n").map(s => s.trim()).filter(Boolean));
      }
    } catch { /* leave defaults */ }
  }

  async function persistBanks(next) {
    setBanksBusy(true); setBanksErr(""); setBanksFlash("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        // Empty string clears the key on the backend; send a JSON array even
        // when empty so future reads land on an explicit [] instead of the
        // legacy-newline branch.
        body: JSON.stringify({ "tuition.banks": JSON.stringify(next) }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      setBanks(next);
    } catch (e) { setBanksErr(e.message); throw e; }
    finally { setBanksBusy(false); }
  }

  async function addBank() {
    setBanksErr(""); setBanksFlash("");
    const name = bankDraft.trim();
    if (!name) { setBanksErr("Type the bank name first (e.g. BDO)."); return; }
    // Case-insensitive dedupe so "BDO" and "bdo" can't both sneak in.
    if (banks.some(b => b.toLowerCase() === name.toLowerCase())) {
      setBanksErr(`"${name}" is already in the list.`);
      return;
    }
    try {
      await persistBanks([...banks, name]);
      setBankDraft("");
      setBanksFlash(`Added "${name}".`);
      setTimeout(() => setBanksFlash(""), 3000);
    } catch { /* error already surfaced */ }
  }

  async function removeBank(name) {
    setBanksErr(""); setBanksFlash("");
    try {
      await persistBanks(banks.filter(b => b !== name));
      setBanksFlash(`Removed "${name}".`);
      setTimeout(() => setBanksFlash(""), 3000);
    } catch { /* error already surfaced */ }
  }

  function addPrefix() {
    setPrefixMsg(""); setPrefixErr("");
    const p = prefixDraft.trim();
    if (!p) { setPrefixErr("Type a prefix first (e.g. S-2026-)."); return; }
    if (prefixes.includes(p)) { setPrefixErr("That prefix is already in the list."); return; }
    const next = [...prefixes, p];
    setPrefixes(next);
    saveStudentPrefixes(next);
    setPrefixDraft("");
    setPrefixMsg(`Added "${p}".`);
  }
  function removePrefix(p) {
    setPrefixMsg(""); setPrefixErr("");
    const next = prefixes.filter(x => x !== p);
    if (next.length === 0) {
      setPrefixErr("Keep at least one prefix — it's used by the Admissions dropdown.");
      return;
    }
    setPrefixes(next);
    saveStudentPrefixes(next);
    setPrefixMsg(`Removed "${p}".`);
  }
  function makeDefault(p) {
    setPrefixMsg(""); setPrefixErr("");
    if (prefixes[0] === p) return;
    const next = [p, ...prefixes.filter(x => x !== p)];
    setPrefixes(next);
    saveStudentPrefixes(next);
    setPrefixMsg(`"${p}" is now the default.`);
  }

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/users`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      setUsers(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);
  useEffect(() => { loadSoaFooter(); }, []);
  useEffect(() => { loadBanks(); }, []);
  // Lazy-load orphan payments only when the Maintenance tab is actually
  // opened AND the caller is SuperDev — the endpoint is now SuperDev-only
  // on the backend, so loading it for a SuperAdmin would 403 and surface
  // a spurious error in the UI. The SQL scan against tuition_payments
  // isn't cheap on a large ledger either, so the gate doubles as a perf
  // win.
  useEffect(() => {
    if (activeTab === "maintenance" && isSuperDev) {
      loadOrphans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSuperDev]);

  // The Employee Defaults tab is visibility-gated on payrollEnabled, so
  // we have to know its value BEFORE any tab is opened. Loading it lazily
  // (only inside the Maintenance effect above) caused the tab to flicker
  // in only after the user clicked Maintenance.
  useEffect(() => {
    loadPayrollEnabled();
    loadHomepageEnabled();
    // Role-permission matrix is loaded for every session so the card
    // renders the persisted state when SuperDev opens Maintenance.
    // Non-SuperDev users never see the card, so the read is cheap
    // but harmless either way.
    loadRolePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.linked) {
      setError("Link the account to a staff member, student, or parent first.");
      return;
    }
    if (!form.username.trim() || !form.password) {
      setError("Username and password are required.");
      return;
    }
    setBusy(true);
    setError("");
    setFlash("");
    try {
      const body = {
        username: form.username,
        email:    form.email,
        password: form.password,
        role:     form.role,
        linkedEntityType: form.linked.type,
        linkedEntityId:   form.linked.id,
        // Legacy: the rest of the backend still reads employeeCode for staff.
        employeeCode: form.linked.type === "STAFF" ? form.linked.code : "",
      };
      // Multi-tenant: when a chain admin picks a specific branch in
      // the dropdown, send it through. Empty string = "use my active
      // school" — the backend's auto-stamp from X-School-Id handles
      // that case so we deliberately don't send schoolId at all.
      if (isChainAdmin && form.schoolId !== "" && form.schoolId != null) {
        body.schoolId = Number(form.schoolId);
      }
      const res = await fetch(`${API_BASE}/api/users`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Create failed (${res.status})`));
      }
      setForm(EMPTY_FORM);
      setFlash(`User "${form.username}" created.`);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  /** When a person is chosen in the popup, prefill the form sensibly. The
   *  role is forced for Student/Parent and suggested for Staff. */
  function handlePick(person) {
    setPickerOpen(false);
    setError("");
    let role = form.role;
    if (person.type === "STUDENT")      role = "Student";
    else if (person.type === "PARENT")  role = "Parent";
    else                                 role = suggestRoleFromStaffType(person.staffType, person.role, currentRole);

    setForm(f => ({
      ...f,
      linked:   person,
      username: f.username.trim() ? f.username : suggestUsername(person.name),
      email:    f.email.trim()    ? f.email    : (person.email || ""),
      role,
    }));
  }

  async function loadOrphans() {
    setOrphansLoading(true); setMaintErr("");
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/orphan-payments`, { headers: authHeaders() });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Load failed (${res.status})`));
      const list = await res.json();
      setOrphans(Array.isArray(list) ? list : []);
    } catch (e) { setMaintErr(e.message); }
    finally { setOrphansLoading(false); }
  }

  async function handleDeleteOrphans() {
    if (orphanConfirm !== "DELETE ORPHANS") {
      setMaintErr('Type "DELETE ORPHANS" exactly to confirm.');
      return;
    }
    setMaintBusy("orphans"); setMaintMsg(""); setMaintErr("");
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/orphan-payments`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Delete failed (${res.status})`));
      const result = await res.json();
      setMaintMsg(`Cleared ${result.paymentsDeleted} orphan payment${result.paymentsDeleted === 1 ? "" : "s"}` +
        (result.requestsCleared > 0 ? ` (and ${result.requestsCleared} stale deletion request${result.requestsCleared === 1 ? "" : "s"})` : "") + ".");
      setOrphanConfirm("");
      await loadOrphans();
    } catch (e) { setMaintErr(e.message); }
    finally { setMaintBusy(""); }
  }

  async function handleExport() {
    setMaintBusy("export"); setMaintMsg(""); setMaintErr("");
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/export`, { headers: authHeaders() });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Export failed (${res.status})`));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `ncla-backup-${today}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMaintMsg("Backup downloaded.");
    } catch (e) { setMaintErr(e.message); }
    finally { setMaintBusy(""); }
  }

  async function handleWipe() {
    if (wipeConfirm !== "WIPE ALL DATA") {
      setMaintErr('Type "WIPE ALL DATA" exactly to confirm.');
      return;
    }
    setMaintBusy("wipe"); setMaintMsg(""); setMaintErr("");
    try {
      const res = await fetch(`${API_BASE}/api/maintenance/wipe`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ confirm: "WIPE ALL DATA" }),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Wipe failed (${res.status})`));
      const result = await res.json();
      setMaintMsg(`Wipe complete — ${result.tablesWiped} tables cleared, ${result.superAdminsKept} SuperAdmin account${result.superAdminsKept === 1 ? "" : "s"} preserved.`);
      setWipeConfirm("");
      await refresh(); // users list will now be just the preserved SuperAdmins
    } catch (e) { setMaintErr(e.message); }
    finally { setMaintBusy(""); }
  }

  async function handleImport() {
    if (!importFile) { setMaintErr("Pick a backup file first."); return; }
    if (importConfirm !== "WIPE AND RESTORE") {
      setMaintErr('Type "WIPE AND RESTORE" exactly to confirm.');
      return;
    }
    setMaintBusy("import"); setMaintMsg(""); setMaintErr("");
    try {
      const text = await importFile.text();
      let parsed;
      try { parsed = JSON.parse(text); }
      catch { throw new Error("File is not valid JSON."); }
      if (!parsed || !parsed.data) {
        throw new Error("File does not look like an NCLA backup (missing 'data' key).");
      }
      const res = await fetch(`${API_BASE}/api/maintenance/import`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(parsed),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Import failed (${res.status})`));
      const result = await res.json();
      setMaintMsg(`Restore complete — ${result.tablesLoaded} tables, ${result.rowsLoaded} rows loaded. Reloading…`);
      setImportFile(null); setImportConfirm("");
      if (importInputRef.current) importInputRef.current.value = "";
      // The restored DB may have a completely different school catalog.
      // Clear the cached school so the Topbar re-resolves against the
      // new schools list instead of filtering by a stale/non-existent id.
      ["sessionStorage", "localStorage"].forEach(k => {
        try {
          const raw = window[k].getItem("user");
          if (!raw) return;
          const obj = JSON.parse(raw);
          delete obj.school;
          delete obj.availableSchools;
          window[k].setItem("user", JSON.stringify(obj));
        } catch { /* ignore */ }
      });
      // Hard reload after a short delay so the success toast is visible.
      setTimeout(() => window.location.assign("/dashboard"), 2000);
    } catch (e) { setMaintErr(e.message); }
    finally { setMaintBusy(""); }
  }

  async function handleDelete(u) {
    if (u.role === "SuperAdmin") return;
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    setError("");
    setFlash("");
    try {
      const res = await fetch(`${API_BASE}/api/users/${u.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Delete failed (${res.status})`));
      }
      setFlash(`User "${u.username}" deleted.`);
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  }

  // Yellow "Root Admin" card. Strong preference for the row explicitly
  // flagged isRoot — that's a stable identity across refreshes. Falls
  // back to "first SuperAdmin found" only when the backend hasn't yet
  // run the migration that adopts one (e.g. very first deploy of this
  // change before a backend restart). Without this, when multiple
  // SuperAdmin rows exist (legacy data, direct DB inserts) the card
  // flickered between them depending on findAll() order, making it
  // look like edits "auto-relinked" when in fact each refresh was
  // showing a different row.
  const superAdmin = users.find(u => u.isRoot === true)
                  || users.find(u => u.role === "SuperAdmin");
  // Everyone else — including non-root SuperAdmins — goes in the
  // regular table so the operator can SEE the duplicates and demote
  // them. Filtering by row id (not by role) means the yellow-card row
  // is excluded but any other SuperAdmin remains visible.
  const others = users
        .filter(u => u.id !== superAdmin?.id)
        .sort((a, b) => (a.username || "").localeCompare(b.username || ""));

  return (
    <div className={s.wrap}>
      <div className={s.header}>
        <div className={s.eyebrow}>SETTINGS</div>
        <h1 className={s.title}>System Settings</h1>
        <p className={s.sub}>
          {isSuperAdmin
            ? "Super Admin view — manage users, academic presets, SOA config, and database maintenance."
            : "Admin view — create accounts for staff, students, and parents; manage academic presets and Statement of Account config. Editing other Admins and database maintenance stay with the Super Admin."}
        </p>
      </div>

      <div className={s.tabs} role="tablist">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`${s.tab} ${activeTab === t.key ? s.tabActive : ""}`}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "users" && (
      <div className={s.grid}>
        <section className={s.card}>
          <div className={s.cardHead}>
            <h2 className={s.cardTitle}>Add User</h2>
            <p className={s.cardHint}>New accounts are created with the role you choose below.</p>
          </div>

          <form className={s.form} onSubmit={handleCreate}>
            <div className={s.field}>
              <span>Linked person</span>
              {form.linked ? (
                <div className={s.linkedChipRow}>
                  {form.linked.photoUrl
                    ? <img src={form.linked.photoUrl} alt="" className={s.linkedAvatarImg} />
                    : <div className={s.linkedAvatar} style={{ background: avatarColor(form.linked.name) }}>
                        {initials(form.linked.name)}
                      </div>
                  }
                  <div className={s.linkedBody}>
                    <div className={s.linkedName}>{form.linked.name}</div>
                    <div className={s.linkedMeta}>
                      {form.linked.type === "STAFF"   && "Staff"}
                      {form.linked.type === "STUDENT" && "Student"}
                      {form.linked.type === "PARENT"  && "Parent"}
                      {form.linked.meta ? ` · ${form.linked.meta}` : ""}
                    </div>
                  </div>
                  <button type="button" className={s.linkedChange} onClick={() => setPickerOpen(true)}>
                    Change
                  </button>
                </div>
              ) : (
                <button type="button" className={s.linkTile} onClick={() => setPickerOpen(true)}>
                  <span className={s.linkTileIcon}>＋</span>
                  <span className={s.linkTileText}>
                    <span className={s.linkTileTitle}>Link to a person</span>
                    <span className={s.linkTileHint}>Required — pick from Staff, Student, or Parent</span>
                  </span>
                </button>
              )}
            </div>

            <label className={s.field}>
              <span>Username</span>
              <input
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. jdoe"
                autoComplete="off"
              />
            </label>

            <label className={s.field}>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="name@school.local"
                autoComplete="off"
              />
            </label>

            <label className={s.field}>
              <span>Temporary password</span>
              <input
                type="text"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="minimum 4 characters"
                autoComplete="new-password"
              />
            </label>

            <div className={s.field}>
              <span>Role</span>
              {form.linked?.type === "STUDENT" || form.linked?.type === "PARENT" ? (
                <div className={s.lockedRole} title="Role is fixed by the linked category">
                  {form.linked.type === "STUDENT" ? "Student" : "Parent / Guardian"}
                  <span className={s.lockedRoleLock}>🔒</span>
                </div>
              ) : (
                <button
                  type="button"
                  className={s.roleChipBtn}
                  disabled={!form.linked}
                  onClick={() => setRolePickerOpen(true)}
                  title={form.linked ? "Change role" : "Link a person first"}>
                  <span className={s.roleChipBody}>
                    <span className={s.roleChipName}>
                      {ASSIGNABLE_ROLES.find(r => r.value === form.role)?.label || form.role}
                    </span>
                    <span className={s.roleChipMeta}>
                      {ASSIGNABLE_ROLES.find(r => r.value === form.role)?.group || "—"}
                      {ASSIGNABLE_ROLES.find(r => r.value === form.role)?.hint
                        ? ` · ${ASSIGNABLE_ROLES.find(r => r.value === form.role).hint}`
                        : ""}
                    </span>
                  </span>
                  <span className={s.roleChipChange}>Change</span>
                </button>
              )}
            </div>

            {/* Branch / school assignment — chain admin only. Branch
                admins don't see this row; the backend stamps the new
                account with their own X-School-Id. The default value
                (empty string) tells the backend to use the active
                school from the header rather than the body value, so
                the chain admin's topbar selector is the source of
                truth unless they explicitly override here. */}
            {isChainAdmin && (
              <label className={s.field}>
                <span>Branch / school</span>
                <select
                  value={form.schoolId}
                  onChange={e => setForm({ ...form, schoolId: e.target.value })}
                  disabled={
                    // Chain-tier roles (SuperDev / SuperAdmin / Owner)
                    // are intentionally not pinned to a branch — the
                    // backend stores their school_id as NULL so they
                    // can switch via the topbar. Lock the dropdown to
                    // "Chain-wide" for those roles so the admin can't
                    // accidentally pin a new SuperAdmin to one branch.
                    form.role === "SuperDev"
                    || form.role === "SuperAdmin"
                    || form.role === "Owner"
                  }
                >
                  {(form.role === "SuperDev"
                    || form.role === "SuperAdmin"
                    || form.role === "Owner") ? (
                    <option value="">Chain-wide (no pinned branch)</option>
                  ) : (
                    <>
                      <option value="">Use my active branch (auto)</option>
                      {Object.values(schoolsById)
                        .filter(sc => sc.active)
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                        .map(sc => (
                          <option key={sc.id} value={sc.id}>
                            {sc.shortName || sc.name}
                          </option>
                        ))}
                    </>
                  )}
                </select>
              </label>
            )}

            <div className={s.formFoot}>
              <button className={s.primaryBtn} type="submit" disabled={busy || !form.linked}>
                {busy ? "Creating…" : "Create user"}
              </button>
              {flash && <span className={s.flash}>{flash}</span>}
              {error && <span className={s.errorText}>{error}</span>}
            </div>
          </form>
        </section>

        <section className={s.card}>
          <div className={s.cardHead}>
            <h2 className={s.cardTitle}>Users</h2>
            <p className={s.cardHint}>
              {loading ? "Loading…" : `${users.length} account${users.length === 1 ? "" : "s"} registered.`}
            </p>
          </div>

          {superAdmin && (
            <div className={`${s.superRow} ${superAdmin.isRoot ? s.superRowRoot : ""}`}>
              {/* Identity column — username + email + (when present) the
                  linked employee summary so the operator knows who this
                  Super Admin actually IS, not just what username they
                  picked. The "🛡️ ROOT ADMIN" pill flags the bootstrap
                  row stably even after a rename. */}
              <div className={s.superRowIdentity}>
                <div className={s.superRowTopLine}>
                  <div className={s.userName}>{superAdmin.username}</div>
                  {superAdmin.isRoot && (
                    <span className={s.rootBadge}
                          title="System Root Admin — created at first boot. Cannot be deleted; role cannot be changed.">
                      <span aria-hidden>🛡️</span>
                      <span>ROOT ADMIN</span>
                    </span>
                  )}
                </div>
                <div className={s.userMeta}>{superAdmin.email || "—"}</div>

                {superAdmin.linkedEntityName ? (
                  /* Linked: show the linked employee inline so the
                     operator can see "this account belongs to X". */
                  <div className={s.superRowLinked}>
                    {superAdmin.linkedEntityPhoto
                      ? <img src={superAdmin.linkedEntityPhoto}
                             alt=""
                             className={s.superRowLinkedPhotoImg} />
                      : <div className={s.superRowLinkedPhoto}
                             style={{ background: avatarColor(superAdmin.linkedEntityName) }}>
                          {initials(superAdmin.linkedEntityName)}
                        </div>
                    }
                    <div className={s.superRowLinkedText}>
                      <div className={s.superRowLinkedLabel}>Linked to</div>
                      <div className={s.superRowLinkedName}>
                        {superAdmin.linkedEntityName}
                      </div>
                      <div className={s.superRowLinkedMeta}>
                        {superAdmin.linkedEntityTitle || superAdmin.linkedEntityType || "—"}
                        {superAdmin.linkedEntityCode ? ` · ${superAdmin.linkedEntityCode}` : ""}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Unlinked: prompt the SuperAdmin to attach this
                     account to an actual employee record (Owner /
                     Principal). The CTA opens the same edit modal
                     where the link can be set. */
                  <button type="button"
                          className={s.superRowLinkCta}
                          onClick={() => setEditingUser(superAdmin)}
                          title="Link this Root Admin to an employee record (Owner / Principal)">
                    <span aria-hidden>🔗</span>
                    <span>Link to Employee</span>
                    <span aria-hidden className={s.superRowLinkCtaArrow}>→</span>
                  </button>
                )}
              </div>

              {/* Right-side controls — role pill + edit pencil. The
                  pencil is the primary entry point for SuperAdmin to
                  rename / rotate password / change linked employee.
                  No Delete button: the Root Admin row is undeletable
                  by design (backend returns 409 even if a request
                  bypasses the UI). */}
              <div className={s.superRowRight}>
                <span className={`${s.rolePill} ${s.rolePillSuper}`}>Super Admin</span>
                <button className={s.editIconBtn}
                        title={superAdmin.isRoot
                          ? "Edit the Root Admin (rename, rotate password, change linked employee)"
                          : "Edit Super Admin"}
                        onClick={() => setEditingUser(superAdmin)}>
                  ✎
                </button>
              </div>
            </div>
          )}

          <div className={s.tableWrap} ref={scrollRef}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Linked to</th>
                  <th>Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {others.length === 0 && !loading && (
                  <tr><td colSpan={4} className={s.empty}>No other users yet. Add one on the left.</td></tr>
                )}
                {others.map(u => {
                  // Admins can only act on non-Admin accounts. SuperAdmins
                  // act on anyone (the SuperAdmin row is rendered above via
                  // the `superAdmin` block, not in this list).
                  const canAct = isSuperAdmin || u.role !== "Admin";
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className={s.userCell}>
                          <div className={s.userCellName}>{u.username}</div>
                          <div className={s.userCellMeta}>{u.email || "—"}</div>
                          {/* Multi-tenant school badge. Null schoolId
                              means chain-tier (SuperAdmin / SuperDev)
                              and renders as "Chain-wide" so the role
                              tier is visible at a glance. */}
                          <div className={s.userCellMeta}>
                            {u.schoolId == null
                              ? <span style={{ color: "#8a6d28", fontWeight: 700 }}>Chain-wide</span>
                              : (schoolsById[u.schoolId]?.shortName
                                 || schoolsById[u.schoolId]?.name
                                 || `School #${u.schoolId}`)}
                          </div>
                        </div>
                      </td>
                      <td>
                        {u.linkedEntityName ? (
                          <div className={s.linkedCell}>
                            {u.linkedEntityPhoto
                              ? <img src={u.linkedEntityPhoto} alt="" className={s.linkedCellAvatarImg} />
                              : <div className={s.linkedCellAvatar} style={{ background: avatarColor(u.linkedEntityName) }}>
                                  {initials(u.linkedEntityName)}
                                </div>
                            }
                            <div className={s.linkedCellText}>
                              <div className={s.linkedCellName}>{u.linkedEntityName}</div>
                              <div className={s.linkedCellMeta}>
                                {u.linkedEntityTitle || u.linkedEntityType || "—"}
                                {u.linkedEntityCode ? ` · ${u.linkedEntityCode}` : ""}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className={s.linkedCellEmpty}>Not linked</span>
                        )}
                      </td>
                      <td><span className={s.rolePill}>{u.role}</span></td>
                      <td className={s.rowActions}>
                        {canAct ? (
                          <>
                            <button className={s.editBtn} onClick={() => setEditingUser(u)}>Edit</button>
                            <button className={s.deleteBtn} onClick={() => handleDelete(u)}>Delete</button>
                          </>
                        ) : (
                          <span style={{ fontSize: "0.8rem", color: "#a39880", fontStyle: "italic" }}>
                            Super Admin only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      )}

      {activeTab === "academic" && (
      <section className={`${s.card} ${s.maintCard}`}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Student ID Prefixes</h2>
          <p className={s.cardHint}>
            Prefixes shown in the Admissions &quot;Student #&quot; dropdown. The first
            prefix is the default; when a prefix is picked, the next number is
            assigned automatically (e.g. {prefixes[0] || defaultStudentPrefix([])}00001).
          </p>
        </div>

        <div className={s.prefixList}>
          {prefixes.map((p, i) => (
            <div key={p} className={s.prefixRow}>
              <span className={s.prefixValue}>{p}</span>
              {i === 0 ? (
                <span className={`${s.rolePill} ${s.rolePillSuper}`}>Default</span>
              ) : (
                <button type="button" className={s.linkedChange}
                        onClick={() => makeDefault(p)}>
                  Set as default
                </button>
              )}
              <button type="button" className={s.deleteBtn}
                      onClick={() => removePrefix(p)}
                      disabled={prefixes.length === 1}
                      title={prefixes.length === 1 ? "Keep at least one prefix" : "Remove"}>
                Remove
              </button>
            </div>
          ))}
        </div>

        <form className={s.prefixAddRow}
              onSubmit={e => { e.preventDefault(); addPrefix(); }}>
          <input
            value={prefixDraft}
            onChange={e => setPrefixDraft(e.target.value)}
            placeholder="e.g. S-2026-"
            autoComplete="off"
            className={s.prefixInput}
          />
          <button className={s.primaryBtn} type="submit">
            Add prefix
          </button>
        </form>
        <div className={s.maintFoot}>
          {prefixMsg && <span className={s.flash}>{prefixMsg}</span>}
          {prefixErr && <span className={s.errorText}>{prefixErr}</span>}
        </div>
      </section>
      )}

      {activeTab === "academic" && <GradingPeriodsCard />}

      {activeTab === "soa" && (
      <section className={`${s.card} ${s.maintCard} ${s.monthPickerCard}`}>
        <div className={s.cardHead}>
          <div className={s.monthPickerHero} role="presentation">
            <div className={s.monthPickerHeroIcon} aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <div>
              <h2 className={s.cardTitle} style={{ margin: 0 }}>Payment Schedule — Start Month</h2>
              <p className={s.cardHint} style={{ margin: 0, marginTop: 4 }}>
                Choose which month the Statement of Account begins. The payment schedule
                will start here and run for the number of months set in each fee structure.
              </p>
            </div>
          </div>
        </div>

        <div className={s.monthPickerWrap}>
          <div className={s.monthPickerLabel}>Select starting month</div>
          <div className={s.monthPickerGrid}>
            {ALL_MONTHS.map(m => (
              <button
                key={m}
                type="button"
                className={`${s.monthPickerBtn}${soaStartMonth === m ? ` ${s.monthPickerBtnActive}` : ""}`}
                onClick={() => setSoaStartMonth(m)}
                disabled={soaLoading}
                aria-pressed={soaStartMonth === m}
              >
                <span className={s.monthPickerAbbr}>{m.slice(0, 3)}</span>
                <span className={s.monthPickerFull}>{m}</span>
              </button>
            ))}
          </div>
          <div className={s.monthPickerPreview}>
            <span className={s.monthPickerPreviewDot} aria-hidden="true" />
            Payment schedule begins in <strong>{soaStartMonth}</strong>
          </div>
        </div>

        <div className={s.formFoot}>
          <button
            type="button"
            className={s.primaryBtn}
            onClick={saveStartMonth}
            disabled={startMonthBusy || soaLoading}>
            {startMonthBusy
              ? <><span className={s.bkSpinner} aria-hidden="true" />Saving…</>
              : "Save start month"}
          </button>
          {startMonthFlash && <span className={s.flash}>{startMonthFlash}</span>}
          {startMonthErr   && <span className={s.errorText}>{startMonthErr}</span>}
        </div>
      </section>
      )}

      {activeTab === "soa" && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Statement of Account — Footer</h2>
          <p className={s.cardHint}>
            Officer name, title, and footer note printed on every Statement of
            Account. School-wide — applies to every student's SoA view and PDF.
          </p>
        </div>

        <div className={s.form}>
          <label className={s.field}>
            <span>Officer name</span>
            <input
              value={soaFooter.officerName}
              onChange={e => setSoaFooter({ ...soaFooter, officerName: e.target.value })}
              placeholder="e.g. Ma. Criste B. Caballero"
              disabled={soaLoading}
              autoComplete="off"
            />
          </label>

          <label className={s.field}>
            <span>Officer title</span>
            <input
              value={soaFooter.officerTitle}
              onChange={e => setSoaFooter({ ...soaFooter, officerTitle: e.target.value })}
              placeholder="e.g. Accounting Officer"
              disabled={soaLoading}
              autoComplete="off"
            />
          </label>

          <label className={s.field}>
            <span>Footer note</span>
            <textarea
              rows={3}
              value={soaFooter.note}
              onChange={e => setSoaFooter({ ...soaFooter, note: e.target.value })}
              placeholder="e.g. For payment, please transact only with the School Cashier…"
              disabled={soaLoading}
            />
          </label>

          <div className={s.formFoot}>
            <button
              type="button"
              className={s.primaryBtn}
              onClick={saveSoaFooter}
              disabled={soaBusy || soaLoading}>
              {soaBusy ? "Saving…" : "Save footer"}
            </button>
            {soaFlash && <span className={s.flash}>{soaFlash}</span>}
            {soaErr && <span className={s.errorText}>{soaErr}</span>}
          </div>
        </div>
      </section>
      )}

      {activeTab === "soa" && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Accepted Banks</h2>
          <p className={s.cardHint}>
            Banks the cashier can pick from when recording a Bank Transfer
            payment. Keeping this as a fixed list avoids &quot;BDO&quot; vs &quot;bdo&quot; vs
            &quot;B.D.O.&quot; drift on the payment history.
          </p>
        </div>

        <div className={s.prefixList}>
          {banks.length === 0 ? (
            <div className={s.cardHint} style={{ fontStyle: "italic" }}>
              No banks configured yet — add the first one below.
            </div>
          ) : banks.map(b => (
            <div key={b} className={s.prefixRow}>
              <span className={s.prefixValue}>{b}</span>
              <button type="button" className={s.deleteBtn}
                      onClick={() => removeBank(b)}
                      disabled={banksBusy}>
                Remove
              </button>
            </div>
          ))}
        </div>

        <form className={s.prefixAddRow}
              onSubmit={e => { e.preventDefault(); addBank(); }}>
          <input
            value={bankDraft}
            onChange={e => setBankDraft(e.target.value)}
            placeholder="e.g. BDO, BPI, LandBank, Metrobank"
            autoComplete="off"
            className={s.prefixInput}
            disabled={banksBusy}
          />
          <button className={s.primaryBtn} type="submit" disabled={banksBusy}>
            {banksBusy ? "Saving…" : "Add bank"}
          </button>
        </form>
        <div className={s.maintFoot}>
          {banksFlash && <span className={s.flash}>{banksFlash}</span>}
          {banksErr && <span className={s.errorText}>{banksErr}</span>}
        </div>
      </section>
      )}

      {activeTab === "employee" && (
      <section className={s.card}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Employee Defaults</h2>
          <p className={s.cardHint}>
            These values apply to every employee who doesn't set their own
            override on the Add/Edit Employee form. Changing them here takes
            effect immediately on the Timesheet and Salary screens.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: "0 4px 8px", maxWidth: 560 }}>
          <TimeOfDayField
            label="Default Time In"
            value={empDefaults["work.defaultSchedStart"] || "08:00"}
            onChange={v => setEmpDefault("work.defaultSchedStart", v)}
          />
          <TimeOfDayField
            label="Default Time Out"
            value={empDefaults["work.defaultSchedEnd"] || "17:00"}
            onChange={v => setEmpDefault("work.defaultSchedEnd", v)}
          />

          {/* Default Overtime Start */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
              Default Overtime Start (minutes after shift end)
            </label>
            <select
              value={String(empDefaults["work.defaultOvertimeMinutes"] || "30")}
              onChange={e => setEmpDefault("work.defaultOvertimeMinutes", e.target.value)}
              style={selectStyle}>
              <option value="0">Immediately (0 min)</option>
              <option value="15">15 minutes after shift end</option>
              <option value="30">30 minutes after shift end</option>
              <option value="45">45 minutes after shift end</option>
              <option value="60">1 hour after shift end</option>
              <option value="90">1 hour 30 minutes after shift end</option>
              <option value="120">2 hours after shift end</option>
            </select>
          </div>

          {/* Default Work Time Policy */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
              Default Work Time Policy
            </label>
            <select
              value={empDefaults["work.defaultPolicy"] || "STRICT"}
              onChange={e => setEmpDefault("work.defaultPolicy", e.target.value)}
              style={selectStyle}>
              <option value="STRICT">Strict Schedule — late measured from scheduled start</option>
              <option value="FLEXIBLE">Flexible Hours — come early, go home early</option>
            </select>
            <span style={{ fontSize: 11, color: "#8a8070" }}>
              Strict penalises late time-in; flexible only measures total worked hours against the scheduled duration.
            </span>
          </div>

          {/* Default Salary Type */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
              Default Salary Type
            </label>
            <select
              value={empDefaults["work.defaultSalaryType"] || "monthly"}
              onChange={e => setEmpDefault("work.defaultSalaryType", e.target.value)}
              style={selectStyle}>
              <option value="monthly">Monthly Rate — salary ÷ actual weekdays this month</option>
              <option value="daily">Daily Rate — salary used directly per day</option>
            </select>
            <span style={{ fontSize: 11, color: "#8a8070" }}>
              Decides how the Salary value entered on each employee is interpreted by the payroll engine. Each employee can override this on their profile.
            </span>
          </div>

          {/* Apply deductions on day-off */}
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={String(empDefaults["work.defaultApplyDeductionsOnDayOff"]).toLowerCase() === "true"}
              onChange={e => setEmpDefault("work.defaultApplyDeductionsOnDayOff", e.target.checked ? "true" : "false")}
              style={{ marginTop: 2 }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
                Apply late / undertime deductions on weekends and holidays
              </div>
              <div style={{ fontSize: 11, color: "#8a8070", marginTop: 2 }}>
                Off (default) — Sundays and Holiday-status entries count only total hours + overtime; late / undertime are ignored. On — day-off entries are penalised like any other working day.
              </div>
            </div>
          </label>

          {/* Default Leave Benefits */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
              Default Leave Benefits
            </label>
            <span style={{ fontSize: 11, color: "#8a8070" }}>
              Seeded onto every new employee. Leave empty to skip — admins can still add leaves manually on each employee.
            </span>
            {defaultLeaveRows.map((row, idx) => (
              <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 36px", gap: 8, alignItems: "center" }}>
                  <input
                    type="text"
                    placeholder="Sick Leave"
                    value={row.name}
                    onChange={e => setDefaultLeaveRows(rs => rs.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder="Days"
                    value={row.days}
                    onChange={e => setDefaultLeaveRows(rs => rs.map((r, i) => i === idx ? { ...r, days: e.target.value } : r))}
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    title="Remove"
                    onClick={() => setDefaultLeaveRows(rs => rs.filter((_, i) => i !== idx))}
                    style={{
                      background: "#fee2e2", border: "1px solid #fecaca", color: "#991b1b",
                      borderRadius: 8, height: 36, cursor: "pointer", fontWeight: 700,
                    }}>×</button>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#5a4e3a" }}>
                  <input
                    type="checkbox"
                    checked={!!row.convertible}
                    onChange={e => setDefaultLeaveRows(rs => rs.map((r, i) => i === idx ? { ...r, convertible: e.target.checked } : r))}
                  />
                  Convertible to cash at end of year
                </label>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setDefaultLeaveRows(rs => [...rs, { name: "", days: "", convertible: false }])}
              style={{
                background: "#fff", border: "1.5px dashed #d8cfc0", borderRadius: 10,
                padding: "8px 12px", cursor: "pointer", fontWeight: 600, color: "#5a4e3a",
              }}>
              + Add Leave
            </button>
          </div>

          {/* Leave Conversion Percentage */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#3d342a" }}>
              Leave Conversion Percentage
            </label>
            <input
              type="number"
              min="0"
              max="200"
              value={empDefaults["work.defaultLeaveConversionPercent"] || "100"}
              onChange={e => setEmpDefault("work.defaultLeaveConversionPercent", e.target.value)}
              style={{ ...inputStyle, maxWidth: 140 }}
            />
            <span style={{ fontSize: 11, color: "#8a8070" }}>
              Applied to unused convertible leave days at year-end. 100% = full daily rate per day; 50% = half, etc.
            </span>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              type="button"
              onClick={saveEmployeeDefaults}
              disabled={empDefaultsBusy}
              style={{
                background: "#c9a227", color: "#fff", border: "none",
                padding: "10px 18px", borderRadius: 10, fontWeight: 700,
                cursor: empDefaultsBusy ? "not-allowed" : "pointer",
              }}>
              {empDefaultsBusy ? "Saving..." : "Save Employee Defaults"}
            </button>
            {empDefaultsFlash && <span style={{ color: "#166534", fontSize: 13 }}>{empDefaultsFlash}</span>}
            {empDefaultsErr   && <span style={{ color: "#991b1b", fontSize: 13 }}>{empDefaultsErr}</span>}
          </div>
        </div>
      </section>
      )}

      {activeTab === "maintenance" && (
      <section className={`${s.card} ${s.maintCard}`}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Maintenance</h2>
          <p className={s.cardHint}>
            {isSuperDev
              ? <>Back up the entire database to a file, or restore a previous backup.
                  Restore <strong>wipes every row</strong> before loading — there is no merge.</>
              : <>Back up the entire database to a file. Keep your backups in a safe place —
                  they contain hashed passwords and personal data.</>}
          </p>
        </div>

        {/* Premium hero banner so the section reads as a dedicated tool, not
            just another form. Static gradient + side accent — no perf cost. */}
        <div className={s.bkHero} role="presentation">
          <div className={s.bkHeroIcon} aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx="12" cy="5" rx="9" ry="3"/>
              <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
              <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6"/>
            </svg>
          </div>
          <div className={s.bkHeroBody}>
            <div className={s.bkHeroTitle}>Database Backup &amp; Restore</div>
            <div className={s.bkHeroSub}>
              Snapshot every table to a single JSON file, or rebuild the system
              from a previous snapshot. {isSuperDev
                ? "Destructive actions require a typed confirmation phrase."
                : "Restoring is reserved for the project developer."}
            </div>
          </div>
        </div>

        <div className={s.bkLayout}>
          {/* Backup — visible to both SuperAdmin and SuperDev. Read-only on
              the database, so it's the only Maintenance op SuperAdmin keeps. */}
          <div className={s.bkPanel}>
            <div className={s.bkPanelHead}>
              <div className={s.bkPanelIcon} aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <div className={s.bkPanelTitle}>Backup database</div>
              <span className={s.bkBadgeSafe}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="3"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Safe
              </span>
            </div>
            <p className={s.bkPanelDesc}>
              Download a JSON snapshot of every table — users, students, grades,
              tuition, payroll, content. Keep it somewhere private; it includes
              hashed passwords and personal data.
            </p>
            <button
              type="button"
              className={s.bkActionBtn}
              onClick={handleExport}
              disabled={!!maintBusy}>
              {maintBusy === "export" ? (
                <>
                  <span className={s.bkSpinner} aria-hidden="true" />
                  Preparing snapshot…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download backup
                </>
              )}
            </button>
          </div>

          {/* Destructive — SuperDev only. SuperAdmin doesn't see them at all
              so the existence of higher privilege never surfaces in the UI.
              The backend MaintenanceController also rejects these endpoints
              for non-SuperDev callers, so this gate is defence-in-depth. */}
          {isSuperDev && (
            <div className={`${s.bkPanel} ${s.bkPanelDanger}`}>
              <div className={s.bkPanelHead}>
                <div className={s.bkPanelIcon} aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 9v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9"/>
                    <polyline points="17 14 12 9 7 14"/>
                    <line x1="12" y1="9" x2="12" y2="21"/>
                  </svg>
                </div>
                <div className={s.bkPanelTitle}>Restore from backup</div>
                <span className={s.bkBadgeDanger}>Destructive</span>
              </div>
              <p className={s.bkPanelDesc}>
                Uploads a backup file and replays every row.{" "}
                <strong>All current data is deleted first</strong> — there is no
                merge. Pick the snapshot, type the confirmation phrase, then run.
              </p>

              <div className={s.bkSteps}>
                <div className={s.bkStep}>
                  <div className={s.bkStepNum}>1</div>
                  <div className={s.bkStepBody}>
                    <div className={s.bkStepLabel}>Choose backup file</div>
                    <label className={`${s.bkFileWrap} ${importFile ? s.bkFileWrapHas : ""}`}>
                      <div className={s.bkFileIcon} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2"
                             strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                      <div className={s.bkFileText}>
                        {importFile
                          ? <>
                              <span className={s.bkFileName}>{importFile.name}</span>
                              <span className={s.bkFileMeta}>
                                {(importFile.size / 1024).toLocaleString(undefined,
                                  { maximumFractionDigits: 1 })} KB · click to replace
                              </span>
                            </>
                          : <>
                              <span className={s.bkFileEmpty}>Click to select a .json backup</span>
                              <span className={s.bkFileMeta}>
                                Produced by the Backup button above
                              </span>
                            </>}
                      </div>
                      <input
                        ref={importInputRef}
                        type="file"
                        accept="application/json,.json"
                        onChange={e => setImportFile(e.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                </div>

                <div className={s.bkStep}>
                  <div className={s.bkStepNum}>2</div>
                  <div className={s.bkStepBody}>
                    <div className={s.bkStepLabel}>
                      Type <code>WIPE AND RESTORE</code> to confirm
                    </div>
                    <div className={s.bkConfirmWrap}>
                      <input
                        type="text"
                        className={`${s.bkConfirmInput} ${importConfirm === "WIPE AND RESTORE" ? s.bkConfirmInputMatched : ""}`}
                        value={importConfirm}
                        onChange={e => setImportConfirm(e.target.value)}
                        placeholder="WIPE AND RESTORE"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {importConfirm === "WIPE AND RESTORE" && (
                        <span className={s.bkConfirmMark} aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="3.5"
                               strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className={`${s.bkActionBtnDanger} ${importFile && importConfirm === "WIPE AND RESTORE" ? s.bkArmed : ""}`}
                onClick={handleImport}
                disabled={!!maintBusy || !importFile || importConfirm !== "WIPE AND RESTORE"}>
                {maintBusy === "import" ? (
                  <>
                    <span className={s.bkSpinner} aria-hidden="true" />
                    Restoring database…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2.2"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 9v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9"/>
                      <polyline points="17 14 12 9 7 14"/>
                      <line x1="12" y1="9" x2="12" y2="21"/>
                    </svg>
                    Wipe &amp; restore
                  </>
                )}
              </button>
            </div>
          )}

          {isSuperDev && (
            <div className={`${s.bkPanel} ${s.bkPanelDanger}`}>
              <div className={s.bkPanelHead}>
                <div className={s.bkPanelIcon} aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                  </svg>
                </div>
                <div className={s.bkPanelTitle}>Wipe all data</div>
                <span className={s.bkBadgeDanger}>Destructive</span>
              </div>
              <p className={s.bkPanelDesc}>
                Clears every row in every table so the system starts fresh.{" "}
                <strong>SuperAdmin accounts are preserved</strong> so you can still
                log in, but their links to staff / student / parent records are
                cleared because those rows are gone.
              </p>

              <div className={s.bkSteps}>
                <div className={s.bkStep}>
                  <div className={s.bkStepNum}>1</div>
                  <div className={s.bkStepBody}>
                    <div className={s.bkStepLabel}>
                      Type <code>WIPE ALL DATA</code> to confirm
                    </div>
                    <div className={s.bkConfirmWrap}>
                      <input
                        type="text"
                        className={`${s.bkConfirmInput} ${wipeConfirm === "WIPE ALL DATA" ? s.bkConfirmInputMatched : ""}`}
                        value={wipeConfirm}
                        onChange={e => setWipeConfirm(e.target.value)}
                        placeholder="WIPE ALL DATA"
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {wipeConfirm === "WIPE ALL DATA" && (
                        <span className={s.bkConfirmMark} aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="3.5"
                               strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className={`${s.bkActionBtnDanger} ${wipeConfirm === "WIPE ALL DATA" ? s.bkArmed : ""}`}
                onClick={handleWipe}
                disabled={!!maintBusy || wipeConfirm !== "WIPE ALL DATA"}>
                {maintBusy === "wipe" ? (
                  <>
                    <span className={s.bkSpinner} aria-hidden="true" />
                    Wiping database…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2.2"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    </svg>
                    Wipe all data
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className={s.bkStatus} aria-live="polite">
          {maintMsg && (
            <div className={`${s.bkToast} ${s.bkToastOk}`}>
              <span className={s.bkToastIcon} aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="3.5"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              {maintMsg}
            </div>
          )}
          {maintErr && (
            <div className={`${s.bkToast} ${s.bkToastErr}`}>
              <span className={s.bkToastIcon} aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="3"
                     strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="8"  x2="12" y2="13"/>
                  <line x1="12" y1="16" x2="12" y2="16.01"/>
                </svg>
              </span>
              {maintErr}
            </div>
          )}
        </div>
      </section>
      )}

      {/* Public homepage toggle — controls whether the marketing
          landing page (/, /news/:id, /programs/:slug) is reachable
          without authentication. Disabling makes the deployment
          read as a members-only system: any visit to the public
          URLs silently redirects to /login. Default is ON so
          existing installs keep their landing page; only an
          explicit "false" flips it off.
          SuperDev-only — the toggle is a system-level switch that
          must not be flipped by the school's day-to-day SuperAdmin.
          The backend SiteContentController also gates the underlying
          key so the change can't be made via a manual API call from
          a SuperAdmin session. */}
      {activeTab === "maintenance" && isSuperDev && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Public Homepage</h2>
          <p className={s.cardHint}>
            Controls whether the school's public landing page —{" "}
            <strong>/</strong>, news articles, and program pages —
            is reachable without signing in. When <strong>OFF</strong>,
            visitors going to the root URL are sent straight to the
            login screen, so the deployment behaves like a
            members-only system. Doesn't affect signed-in users; an
            already-authenticated visit to <strong>/</strong> still
            lands on the dashboard. Safe to flip on or off — it
            doesn't touch any school data.
          </p>
        </div>

        <div style={{ padding: "12px 18px 18px" }}>
          <label style={{ display: "inline-flex", alignItems: "center",
                          gap: 10, cursor: homepageBusy ? "wait" : "pointer",
                          fontSize: "0.95rem" }}>
            <input
              type="checkbox"
              checked={homepageEnabled}
              disabled={homepageBusy}
              onChange={e => saveHomepageEnabled(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span>
              <strong>Enable public homepage</strong>
              <span style={{ color: "#8a8070", marginLeft: 8, fontSize: "0.85rem" }}>
                {homepageEnabled
                  ? "ON — / serves the public landing page"
                  : "OFF — / redirects to /login"}
              </span>
            </span>
          </label>
          {homepageFlash && (
            <div className={s.flash} style={{ marginTop: 10 }}>{homepageFlash}</div>
          )}
          {homepageErr && (
            <div className={s.errorText} style={{ marginTop: 10 }}>{homepageErr}</div>
          )}
        </div>
      </section>
      )}

      {/* Role Permissions matrix — SuperDev only. Lets the developer
          grant specific roles the ability to add schools or manage
          content WITHOUT raising them to a full Admin / SuperAdmin.
          The card is invisible to SuperAdmin (they only see Backup
          + Wipe / Restore + Orphan Payments) so the privilege-grant
          surface stays in the developer's hands. */}
      {activeTab === "maintenance" && isSuperDev && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Role Permissions</h2>
          <p className={s.cardHint}>
            Grant specific roles the ability to perform privileged
            actions without raising them to Admin. Each checkbox is
            <strong> purely additive</strong> over the hard-coded
            baseline (SuperDev / SuperAdmin / Admin keep their
            built-in privileges either way). Hard delete of a school
            stays SuperDev-only regardless of what's checked here.
            Changes save immediately and take effect on the next
            request — no reboot or re-login required.
          </p>
        </div>

        <div className={s.permsGrid}>
          {/* Add Schools — grants chain-admin write privileges. */}
          <div className={s.permsPanel}>
            <div className={s.permsPanelTitle}>Allow adding multiple schools</div>
            <p className={s.permsPanelHint}>
              Roles checked here can create / edit / activate / deactivate
              branches via Settings → Schools. Hard delete remains
              SuperDev-only. SuperDev is always allowed and not listed.
            </p>
            <div className={s.permsRoleGrid}>
              {ALL_ROLES_FOR_MATRIX.map(role => {
                const checked = permsAddSchools.includes(role);
                const cls = [
                  s.permsRoleRow,
                  checked   ? s.checked : "",
                  permsBusy ? s.busy    : "",
                ].filter(Boolean).join(" ");
                return (
                  <label key={role} className={cls}>
                    <input type="checkbox"
                           className={s.permsCheckbox}
                           checked={checked}
                           disabled={permsBusy}
                           onChange={() => togglePermissionRole("addSchools", role)} />
                    <span>{role}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Manage Content — grants article/content write privileges. */}
          <div className={s.permsPanel}>
            <div className={s.permsPanelTitle}>Allow creating contents</div>
            <p className={s.permsPanelHint}>
              Roles checked here can create / edit / delete articles
              and other public-site content. Admin / SuperAdmin / SuperDev
              always have this and aren't listed.
            </p>
            <div className={s.permsRoleGrid}>
              {ALL_ROLES_FOR_MATRIX.filter(r => r !== "Admin").map(role => {
                const checked = permsManageContent.includes(role);
                const cls = [
                  s.permsRoleRow,
                  checked   ? s.checked : "",
                  permsBusy ? s.busy    : "",
                ].filter(Boolean).join(" ");
                return (
                  <label key={role} className={cls}>
                    <input type="checkbox"
                           className={s.permsCheckbox}
                           checked={checked}
                           disabled={permsBusy}
                           onChange={() => togglePermissionRole("manageContent", role)} />
                    <span>{role}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Login gate — SEMANTICALLY INVERTED from the other two
               panels. Empty backend list = everyone can log in
               (default ships behaviour). Unchecking a role ADDS it
               to the disabled list and refuses login at the
               AuthController. The four LOGIN_ALWAYS_ALLOWED roles
               (SuperDev / SuperAdmin / Owner / Admin) are hidden so
               the operator can never lock themselves out — even if
               someone tampers with the matrix in the DB directly,
               the backend's hard-coded baseline ignores those role
               names in the disabled list. */}
          <div className={s.permsPanel}>
            <div className={s.permsPanelTitle}>Allow logging in</div>
            <p className={s.permsPanelHint}>
              Checked roles can log in (the default for every fresh
              install). Uncheck a role to <strong>disable login</strong>{" "}
              for everyone with that role — useful for trimming the
              system to the seats a client actually paid for.
              SuperDev / SuperAdmin / Owner / Admin can never be
              disabled and aren't listed (self-lockout guard).
              Existing sessions stay live until the user signs out;
              only NEW logins are blocked.
            </p>
            <div className={s.permsRoleGrid}>
              {ALL_ROLES_FOR_MATRIX
                  .filter(r => r !== "Admin" && r !== "Owner")
                  .map(role => {
                const blocked = permsDisabledLogin.includes(role);
                const checked = !blocked;   // inverted semantic
                const cls = [
                  s.permsRoleRow,
                  checked   ? s.checked : "",
                  permsBusy ? s.busy    : "",
                ].filter(Boolean).join(" ");
                return (
                  <label key={role} className={cls}>
                    <input type="checkbox"
                           className={s.permsCheckbox}
                           checked={checked}
                           disabled={permsBusy}
                           onChange={() => togglePermissionRole("allowLogin", role)} />
                    <span>{role}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {(permsFlash || permsErr) && (
          <div className={s.permsStatus}>
            {permsFlash && <div className={s.flash}>{permsFlash}</div>}
            {permsErr   && <div className={s.errorText}>{permsErr}</div>}
          </div>
        )}
      </section>
      )}

      {/* Optional Payroll module — ported from inventorySales. Hidden by
          default; flipping ON shows /salary, /timesheet, /payroll-calendar
          routes + their topbar nav. The pages render against existing
          /api/employees data; full functionality (timesheets, calendar
          events) requires Phase 2 backend endpoints.
          SuperDev-only — same reasoning as the Public Homepage toggle.
          Module-level feature flags belong with the developer, not with
          the school's SuperAdmin. */}
      {activeTab === "maintenance" && isSuperDev && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Payroll Module</h2>
          <p className={s.cardHint}>
            Optional HR / Payroll / Timesheet / Calendar feature. When
            enabled, SuperAdmins see new <strong>Salary</strong>,{" "}
            <strong>Timesheet</strong>, and <strong>HR Calendar</strong> pages
            in the topbar. Disabling hides every entry point and the routes
            return to the dashboard. Safe to flip on or off — it doesn't
            touch school data.
          </p>
        </div>

        <div style={{ padding: "12px 18px 18px" }}>
          <label style={{ display: "inline-flex", alignItems: "center",
                          gap: 10, cursor: payrollBusy ? "wait" : "pointer",
                          fontSize: "0.95rem" }}>
            <input
              type="checkbox"
              checked={payrollEnabled}
              disabled={payrollBusy}
              onChange={e => savePayrollEnabled(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span>
              <strong>Enable Payroll module</strong>
              <span style={{ color: "#8a8070", marginLeft: 8, fontSize: "0.85rem" }}>
                {payrollEnabled ? "ON — visible to SuperAdmin" : "OFF — completely hidden"}
              </span>
            </span>
          </label>
          {payrollFlash && (
            <div className={s.flash} style={{ marginTop: 10 }}>{payrollFlash}</div>
          )}
          {payrollErr && (
            <div className={s.errorText} style={{ marginTop: 10 }}>{payrollErr}</div>
          )}
        </div>
      </section>
      )}

      {/* Cloudflare R2 Migration — SuperDev only. The whole storage
          backend migration is a developer-level operation, not a
          routine maintenance task, so it sits behind the same gate
          as Role Permissions + Orphan Payments. The panel itself
          re-checks the role internally as defence-in-depth. */}
      {activeTab === "maintenance" && isSuperDev && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Cloudflare R2 Migration</h2>
          <p className={s.cardHint}>
            Move every image / logo / media URL from Cloudinary to
            Cloudflare R2. Three steps —
            <strong> Scan</strong> previews what will move,
            <strong> Migrate</strong> copies in batches with per-item
            atomic safety,
            <strong> Verify</strong> confirms the cutover. The original
            Cloudinary assets are NOT deleted by this flow — rollback
            is a single env-var flip until you run the manual Step-8
            cleanup.
          </p>
        </div>
        <div style={{ padding: "12px 18px 18px" }}>
          <CloudflareMigrationPanel role={currentRole} />
        </div>
      </section>
      )}

      {/* Orphan Payments — SuperDev only. The DELETE endpoint is
          destructive and the LIST endpoint is SuperDev-only on the
          backend; rendering the card for SuperAdmin would just be a
          dead-end with a 403 toast. */}
      {activeTab === "maintenance" && isSuperDev && (
      <section className={`${s.card} ${s.maintCard}`} style={{ marginTop: 12 }}>
        <div className={s.cardHead}>
          <h2 className={s.cardTitle}>Orphan Payments</h2>
          <p className={s.cardHint}>
            Tuition payments whose invoice was deleted before the cascade was
            put in place. They show up in the Reports → Finance breakdown
            as &quot;Orphan payment · invoice #X (deleted)&quot;. Cleaning them
            here removes the rows and any pending deletion requests that
            point at them.
          </p>
        </div>

        {orphansLoading ? (
          <div style={{ padding: 12, color: "#8a8070", fontStyle: "italic" }}>Scanning…</div>
        ) : orphans.length === 0 ? (
          <div style={{ padding: 12, color: "#1f6a2d", fontWeight: 600 }}>
            ✓ No orphan payments found — the ledger is clean.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontSize: "0.9rem", color: "#3d342a" }}>
              <b>{orphans.length}</b> orphan payment{orphans.length === 1 ? "" : "s"} found.
            </div>
            <div style={{
              maxHeight: 280, overflowY: "auto",
              border: "1px solid #e5dfd4", borderRadius: 8, padding: 4,
              background: "#fafaf6",
            }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#8a8070",
                               borderBottom: "1px solid #e5dfd4" }}>
                    <th style={{ padding: "6px 8px" }}>Payment #</th>
                    <th style={{ padding: "6px 8px" }}>Invoice #</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Amount</th>
                    <th style={{ padding: "6px 8px" }}>Method</th>
                    <th style={{ padding: "6px 8px" }}>Recorded by</th>
                    <th style={{ padding: "6px 8px" }}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map(o => (
                    <tr key={o.id} style={{ borderBottom: "1px solid #f0ebe0" }}>
                      <td style={{ padding: "6px 8px", fontVariantNumeric: "tabular-nums" }}>{o.id}</td>
                      <td style={{ padding: "6px 8px", fontVariantNumeric: "tabular-nums",
                                   color: "#c0392b" }}>
                        #{o.invoice_id ?? "—"}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right",
                                   fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
                        ₱{Number(o.amount || 0).toLocaleString(undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {o.method || "—"}
                        {o.bank_name ? ` · ${o.bank_name}` : ""}
                      </td>
                      <td style={{ padding: "6px 8px" }}>{o.recorded_by_username || "—"}</td>
                      <td style={{ padding: "6px 8px", color: "#8a8070" }}>
                        {o.paid_at ? new Date(o.paid_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <label className={s.field} style={{ marginTop: 12 }}>
              <span>Type <code>DELETE ORPHANS</code> to confirm</span>
              <input
                type="text"
                value={orphanConfirm}
                onChange={e => setOrphanConfirm(e.target.value)}
                placeholder="DELETE ORPHANS"
                autoComplete="off"
              />
            </label>
            <button
              className={s.dangerBtn}
              onClick={handleDeleteOrphans}
              disabled={!!maintBusy || orphanConfirm !== "DELETE ORPHANS"}>
              {maintBusy === "orphans"
                ? "Cleaning…"
                : `Delete all ${orphans.length} orphan payment${orphans.length === 1 ? "" : "s"}`}
            </button>
          </>
        )}
      </section>
      )}

      {pickerOpen && (
        <PersonPickerModal
          linkedKeys={linkedKeys}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
        />
      )}

      {rolePickerOpen && (
        <RolePickerModal
          value={form.role}
          allowedValues={rolesAssignableBy(currentRole)}
          onClose={() => setRolePickerOpen(false)}
          onPick={v => { setForm(f => ({ ...f, role: v })); setRolePickerOpen(false); }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          linkedKeys={linkedKeys}
          schoolsById={schoolsById}
          onClose={() => setEditingUser(null)}
          onSaved={(msg) => {
            setEditingUser(null);
            setFlash(msg);
            setError("");
            refresh();
          }}
        />
      )}
    </div>
  );
}
